import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { 
  MapContainer, 
  TileLayer, 
  Marker, 
  Popup, 
  Polyline,
  Circle,
  Tooltip,
  useMapEvents 
} from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Locate, LocateFixed, Star, Clock, ArrowLeftRight, Navigation } from 'lucide-react';
import { useMobile, useTouch } from '../hooks/useMobile';
import MessagePinPopup from './MessagePinPopup';
import MessagePinErrorBoundary from './MessagePinErrorBoundary';
import { useCompassRaw, getScreenAngle } from '../hooks/useCompass';
import { getImageUrl } from '../config/runtime';
import { normalizeProjectGeolocation, getProjectTrailPoints, projectHasTrail } from '../utils/projectLocation';
import LocationConsentService from '../services/LocationConsentService';

// Fix for default markers in react-leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Custom project marker icon with number
const createNumberedIcon = (number, difficulty = 'moderate', isActive = false) => {
  const colors = {
    easy: '#10B981',    // Green
    moderate: '#F59E0B', // Yellow
    hard: '#EF4444'     // Red
  };
  
  const color = colors[difficulty] || colors.moderate;
  const size = isActive ? 40 : 32;
  const borderWidth = isActive ? 4 : 3;
  const borderColor = isActive ? '#3B82F6' : 'white'; // Blue border when active
  
  return new L.DivIcon({
    html: `
      <div style="
        background-color: ${isActive ? '#3B82F6' : color};
        color: white;
        border-radius: 50%;
        width: ${size}px;
        height: ${size}px;
        display: flex;
        align-items: center;
        justify-content: center;
        font-weight: bold;
        font-size: ${isActive ? 16 : 14}px;
        border: ${borderWidth}px solid ${borderColor};
        box-shadow: ${isActive ? '0 4px 12px rgba(59,130,246,0.5)' : '0 2px 6px rgba(0,0,0,0.3)'};
        transform: ${isActive ? 'scale(1.1)' : 'scale(1)'};
        transition: all 0.2s ease;
      ">${number}</div>
    `,
    className: 'custom-div-icon',
    iconSize: [size, size],
    iconAnchor: [size/2, size],
    popupAnchor: [0, -size],
  });
};

// Simple marker icon for individual projects (not clusters)
// All pins same color by default, selected pin is blue
const createProjectMarkerIcon = (isActive = false) => {
  const defaultColor = '#1F2937'; // Dark gray - neutral for all pins
  const activeColor = '#3B82F6';  // Blue for selected
  const color = isActive ? activeColor : defaultColor;
  const size = isActive ? 36 : 28;
  
  return new L.DivIcon({
    html: `
      <div style="
        background-color: ${color};
        border-radius: 50% 50% 50% 0;
        width: ${size}px;
        height: ${size}px;
        transform: rotate(-45deg);
        border: 3px solid white;
        box-shadow: ${isActive ? '0 4px 12px rgba(59,130,246,0.5)' : '0 2px 6px rgba(0,0,0,0.3)'};
      ">
        <div style="
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          transform: rotate(45deg);
        ">
          <div style="
            width: ${isActive ? 10 : 8}px;
            height: ${isActive ? 10 : 8}px;
            background: white;
            border-radius: 50%;
          "></div>
        </div>
      </div>
    `,
    className: 'custom-div-icon',
    iconSize: [size, size],
    iconAnchor: [size/2, size],
    popupAnchor: [0, -size],
  });
};

/**
 * Creates a discovery pin icon - shows photo thumbnail if available,
 * otherwise a clean marker. Badge shows count for clusters.
 * 
 * Design principles (Tufte + Vignelli):
 * - Show the data directly (photo = instant recognition)
 * - Minimal chrome, maximum content
 * - Clear visual hierarchy (photo > badge > marker)
 */
const createDiscoveryIcon = (spot, isSelected = false) => {
  const count = Math.min(99, Math.max(1, spot?.count || 1));
  // IMPORTANT: Process image URL with getImageUrl for proper path prefix
  const rawImageUrl = spot?.imageUrl;
  const imageUrl = rawImageUrl ? getImageUrl(rawImageUrl) : null;
  const hasPhoto = Boolean(imageUrl);
  const isCluster = count > 1;
  
  // Pin size based on importance (slightly larger when selected)
  const baseSize = isCluster ? 44 : 36;
  const size = isSelected ? baseSize + 8 : baseSize;
  
  // Border color changes when selected
  const borderColor = isSelected ? '#10b981' : 'white';
  const borderWidth = isSelected ? '3.5px' : '2.5px';
  const shadowStyle = isSelected 
    ? '0 4px 16px rgba(16,185,129,0.4), 0 2px 6px rgba(0,0,0,0.2)' 
    : '0 3px 12px rgba(0,0,0,0.25), 0 1px 3px rgba(0,0,0,0.15)';
  
  if (hasPhoto) {
    // Photo pin - the image IS the marker (Tufte: data-ink ratio)
    return new L.DivIcon({
      html: `
        <div style="
          position: relative;
          width: ${size}px;
          height: ${size}px;
          ${isSelected ? 'transform: scale(1.1); transition: transform 0.15s ease-out;' : ''}
        ">
          <div style="
            width: ${size}px;
            height: ${size}px;
            border-radius: 8px;
            overflow: hidden;
            border: ${borderWidth} solid ${borderColor};
            box-shadow: ${shadowStyle};
            background: #e5e7eb;
          ">
            <img 
              src="${imageUrl}" 
              style="width:100%; height:100%; object-fit:cover;"
              onerror="this.style.display='none'; this.parentNode.innerHTML='<div style=\\'display:flex;align-items:center;justify-content:center;width:100%;height:100%;background:#10b981;color:white;font-size:14px;\\'>🌿</div>'"
            />
          </div>
          ${isCluster ? `
            <div style="
              position: absolute;
              top: -6px;
              right: -6px;
              min-width: 18px;
              height: 18px;
              background: linear-gradient(135deg, #10b981, #059669);
              color: white;
              font-size: 10px;
              font-weight: 700;
              border-radius: 9px;
              display: flex;
              align-items: center;
              justify-content: center;
              padding: 0 4px;
              border: 2px solid white;
              box-shadow: 0 2px 4px rgba(0,0,0,0.2);
            ">${count}</div>
          ` : ''}
        </div>
      `,
      className: `discovery-photo-icon ${isSelected ? 'discovery-selected' : ''}`,
      iconSize: [size, size],
      iconAnchor: [size / 2, size / 2],
      popupAnchor: [0, -size / 2 - 4],
    });
  }
  
  // No photo - clean minimal marker
  const circleSize = isCluster ? 32 : 26;
  const selectedCircleSize = isSelected ? circleSize + 6 : circleSize;
  return new L.DivIcon({
    html: `
      <div style="
        position: relative;
        display: flex;
        flex-direction: column;
        align-items: center;
        ${isSelected ? 'transform: scale(1.15); transition: transform 0.15s ease-out;' : ''}
      ">
        <div style="
          width: ${selectedCircleSize}px;
          height: ${selectedCircleSize}px;
          border-radius: 50%;
          background: linear-gradient(135deg, #10b981, #059669);
          border: ${borderWidth} solid ${borderColor};
          box-shadow: ${shadowStyle};
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: ${isCluster ? 12 : 10}px;
        ">
          ${isCluster ? count : '🌿'}
        </div>
      </div>
    `,
    className: `discovery-marker-icon ${isSelected ? 'discovery-selected' : ''}`,
    iconSize: [selectedCircleSize, selectedCircleSize],
    iconAnchor: [selectedCircleSize / 2, selectedCircleSize / 2],
    popupAnchor: [0, -selectedCircleSize / 2 - 4],
  });
};

// Legacy alias for backwards compatibility


// Custom user location icon with compass direction
const createUserLocationWithCompassIcon = (heading = null) => {
  const size = 40;
  const center = size / 2;
  
  // If no heading available, use simple location icon
  if (heading === null) {
    return new L.DivIcon({
      html: `
        <div style="
          position: relative;
          width: ${size}px;
          height: ${size}px;
          display: flex;
          align-items: center;
          justify-content: center;
        ">
          <div style="
            background-color: #3B82F6;
            border: 3px solid white;
            border-radius: 50%;
            width: 20px;
            height: 20px;
            box-shadow: 0 2px 8px rgba(0,0,0,0.3);
            position: relative;
            z-index: 2;
          ">
            <div style="
              background-color: white;
              border-radius: 50%;
              width: 8px;
              height: 8px;
              position: absolute;
              top: 50%;
              left: 50%;
              transform: translate(-50%, -50%);
            "></div>
          </div>
        </div>
      `,
      className: 'user-location-icon',
      iconSize: [size, size],
      iconAnchor: [center, center],
      popupAnchor: [0, -center],
    });
  }
  
  // With compass heading - create directional cone
  const coneLength = 40;  // Made bigger
  const coneWidth = 35;   // Made bigger
  
  return new L.DivIcon({
    html: `
      <div style="
        position: relative;
        width: ${size}px;
        height: ${size}px;
        display: flex;
        align-items: center;
        justify-content: center;
        transform: rotate(${(heading + 270) % 360}deg);
      ">
        <!-- Direction cone (inverted - narrow end at user dot) -->
        <div style="
          position: absolute;
          width: 0;
          height: 0;
          border-left: ${coneWidth/2}px solid transparent;
          border-right: ${coneWidth/2}px solid transparent;
          border-top: ${coneLength}px solid rgba(59, 130, 246, 0.5);
          bottom: 10px;
          left: 50%;
          transform: translateX(-50%);
          z-index: 1;
        "></div>
        
        <!-- User location dot -->
        <div style="
          background-color: #3B82F6;
          border: 3px solid white;
          border-radius: 50%;
          width: 20px;
          height: 20px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.3);
          position: relative;
          z-index: 2;
          transform: rotate(${-((heading + 270) % 360)}deg);
        ">
          <div style="
            background-color: white;
            border-radius: 50%;
            width: 8px;
            height: 8px;
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
          "></div>
        </div>
      </div>
    `,
    className: 'user-location-compass-icon',
    iconSize: [size, size],
    iconAnchor: [center, center],
    popupAnchor: [0, -center],
  });
};

// Custom waypoint icon for creation mode
const waypointIcon = new L.Icon({
  iconUrl: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjQiIGhlaWdodD0iMjQiIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPGNpcmNsZSBjeD0iMTIiIGN5PSIxMiIgcj0iMTAiIGZpbGw9IiNCMzMzRkYiLz4KPHBhdGggZD0iTTEyIDhWMTYiIHN0cm9rZT0id2hpdGUiIHN0cm9rZS13aWR0aD0iMiIgc3Ryb2tlLWxpbmVjYXA9InJvdW5kIi8+CjxwYXRoIGQ9Ik04IDEySDEyIiBzdHJva2U9IndoaXRlIiBzdHJva2Utd2lkdGg9IjIiIHN0cm9rZS1saW5lY2FwPSJyb3VuZCIvPgo8L3N2Zz4=',
  iconSize: [24, 24],
  iconAnchor: [12, 24],
  popupAnchor: [0, -24],
});

// Custom AI suggestion icon
// Numbered station pin for objective-driven routes (the "family map" layer).
// completed → green check; current → highlighted with a pulse ring.
const createObjectivePinIcon = ({ number, completed, current }) => {
  const bg = completed ? '#10B981' : current ? '#6366F1' : '#1F2937';
  const size = current ? 34 : 28;
  const pulse = current
    ? '<div style="position:absolute;inset:-6px;border-radius:50%;border:3px solid rgba(99,102,241,0.55);animation:okio-station-pulse 1.6s ease-out infinite;"></div>'
    : '';
  const label = completed ? '✓' : String(number);
  return new L.DivIcon({
    html: `
      <div style="position:relative;width:${size}px;height:${size}px;">
        ${pulse}
        <div style="
          background:${bg};
          border:3px solid white;
          border-radius:50%;
          width:100%;
          height:100%;
          display:flex;
          align-items:center;
          justify-content:center;
          box-shadow:0 2px 8px rgba(0,0,0,0.35);
          color:white;
          font-size:13px;
          font-weight:700;
        ">${label}</div>
      </div>
    `,
    className: 'objective-station-pin',
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });
};

const createAISuggestionIcon = (confidence = 0.7) => {
  // Color based on confidence: high = green, medium = blue, low = orange
  const color = confidence > 0.8 ? '#10B981' : confidence > 0.6 ? '#3B82F6' : '#F59E0B';
  
  return new L.DivIcon({
    html: `
      <div style="
        background-color: ${color};
        border: 3px solid white;
        border-radius: 8px;
        width: 28px;
        height: 28px;
        display: flex;
        align-items: center;
        justify-content: center;
        box-shadow: 0 2px 8px rgba(0,0,0,0.25);
        position: relative;
      ">
        <div style="color: white; font-size: 14px; font-weight: bold;">🎯</div>
        <div style="
          position: absolute;
          bottom: -6px;
          right: -6px;
          background: white;
          border-radius: 50%;
          width: 16px;
          height: 16px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 10px;
          box-shadow: 0 1px 3px rgba(0,0,0,0.2);
        ">🤖</div>
      </div>
    `,
    className: 'custom-div-icon ai-suggestion-icon',
    iconSize: [28, 28],
    iconAnchor: [14, 14]
  });
};

// Icon cache to prevent memory leaks and improve performance
const iconCache = new Map();

// Create message pin icon based on pin type with caching
const createMessagePinIcon = (pin, size = 40) => {
  const { type, pinType, imageUrl } = pin;
  const color = pinType?.color || '#3B82F6';
  
  // Create cache key based on pin properties
  const cacheKey = `${type}-${size}-${color}-${imageUrl || 'no-image'}`;
  
  // Return cached icon if available
  if (iconCache.has(cacheKey)) {
    return iconCache.get(cacheKey);
  }
  
  let icon;
  switch (type) {
    case 'PHOTO':
      // Apply getImageUrl for proper path prefix before creating icon
      icon = createPhotoPinIcon(getImageUrl(pin.imageUrl), size, color);
      break;
    case 'AUDIO':
      icon = createGenericMessagePinIcon('🎵', size, color);
      break;
    case 'DISCOVERY':
      icon = createGenericMessagePinIcon('⭐', size, color);
      break;
    case 'MILESTONE':
      icon = createGenericMessagePinIcon('🚩', size, color);
      break;
    default:
      icon = createGenericMessagePinIcon('📍', size, color);
      break;
  }
  
  // Cache the icon (limit cache size to prevent memory issues)
  if (iconCache.size >= 100) {
    const firstKey = iconCache.keys().next().value;
    iconCache.delete(firstKey);
  }
  iconCache.set(cacheKey, icon);
  
  return icon;
};

// Helper function to sanitize URL for safe HTML embedding
const sanitizeImageUrl = (url) => {
  if (!url || typeof url !== 'string') return '';
  
  // Basic URL validation - must be http/https or relative path
  /* eslint-disable-next-line no-script-url */
  if (url.startsWith('javascript:') || url.startsWith('data:') || url.includes('<') || url.includes('>')) {
    console.warn('Potentially unsafe image URL blocked:', url);
    return '';
  }
  
  // HTML entity encoding for safety
  return url.replace(/["'<>&]/g, (char) => {
    const entities = {
      '"': '&quot;',
      "'": '&#x27;',
      '<': '&lt;',
      '>': '&gt;',
      '&': '&amp;'
    };
    return entities[char] || char;
  });
};

// Create photo pin icon with thumbnail
const createPhotoPinIcon = (imageUrl, size = 40, borderColor = '#3B82F6') => {
  const safeImageUrl = sanitizeImageUrl(imageUrl);
  
  // If URL is not safe, create fallback icon
  if (!safeImageUrl) {
    return createGenericMessagePinIcon('📷', size, borderColor);
  }
  
  return new L.DivIcon({
    html: `
      <div style="
        position: relative;
        width: ${size}px;
        height: ${size}px;
        border-radius: 50%;
        border: 3px solid ${borderColor};
        background: white;
        box-shadow: 0 3px 10px rgba(0,0,0,0.3);
        overflow: hidden;
        display: flex;
        align-items: center;
        justify-content: center;
      ">
        <img 
          src="${safeImageUrl}" 
          style="
            width: 100%;
            height: 100%;
            object-fit: cover;
            border-radius: 50%;
          "
          onerror="this.style.display='none'; this.nextElementSibling.style.display='flex';"
        />
    
      </div>
    `,
    className: 'photo-pin-marker',
    iconSize: [size, size],
    iconAnchor: [size/2, size],
    popupAnchor: [0, -size],
  });
};

// Create generic message pin icon
const createGenericMessagePinIcon = (emoji, size = 40, backgroundColor = '#3B82F6') => {
  return new L.DivIcon({
    html: `
      <div style="
        background-color: ${backgroundColor};
        color: white;
        border-radius: 50%;
        width: ${size}px;
        height: ${size}px;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: ${size * 0.4}px;
        border: 3px solid white;
        box-shadow: 0 3px 10px rgba(0,0,0,0.3);
      ">${emoji}</div>
    `,
    className: 'message-pin-marker',
    iconSize: [size, size],
    iconAnchor: [size/2, size],
    popupAnchor: [0, -size],
  });
};

// Create cluster icon for multiple message pins with caching
const createMessageClusterIcon = (cluster, size = 50) => {
  const count = cluster.count || cluster.pins?.length || 0;
  const primaryType = cluster.centerPin?.type || 'PHOTO';
  const color = cluster.centerPin?.pinType?.color || '#3B82F6';
  
  // Create cache key for cluster icons
  const cacheKey = `cluster-${primaryType}-${count}-${size}-${color}`;
  
  // Return cached icon if available
  if (iconCache.has(cacheKey)) {
    return iconCache.get(cacheKey);
  }
  
  let emoji = '📍';
  switch (primaryType) {
    case 'PHOTO': emoji = '📷'; break;
    case 'AUDIO': emoji = '🎵'; break;
    case 'DISCOVERY': emoji = '⭐'; break;
    case 'MILESTONE': emoji = '🚩'; break;
    default: emoji = '📍'; break;
  }
  
  const icon = new L.DivIcon({
    html: `
      <div style="
        background-color: ${color};
        color: white;
        border-radius: 50%;
        width: ${size}px;
        height: ${size}px;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        border: 3px solid white;
        box-shadow: 0 3px 10px rgba(0,0,0,0.3);
        font-weight: bold;
      ">
        <div style="font-size: ${size * 0.3}px; line-height: 1;">${emoji}</div>
        <div style="font-size: ${size * 0.25}px; line-height: 1;">${count}</div>
      </div>
    `,
    className: 'message-cluster-marker',
    iconSize: [size, size],
    iconAnchor: [size/2, size],
    popupAnchor: [0, -size],
  });
  
  // Cache the icon
  if (iconCache.size >= 100) {
    const firstKey = iconCache.keys().next().value;
    iconCache.delete(firstKey);
  }
  iconCache.set(cacheKey, icon);
  
  return icon;
};

// Component for handling location tracking, zoom changes, and map clicks
const MapEventHandler = ({ 
  onLocationFound, 
  onLocationError,
  userLocation, 
  onZoomChange, 
  skipLocationUpdate = false,
  mode = 'view',
  onMapClick = null,
  enableLocationTracking = false,
  autoFollow = false,
  onUserInteracted = null,
  ignoreUserInteraction = false,
  onViewportChange = null
}) => {
  // Debounce viewport changes to avoid too many callbacks during dragging
  const viewportChangeTimeoutRef = useRef(null);
  const hasInitializedViewportRef = useRef(false);
  const getBoundsPayload = useCallback((leafletMap) => {
    if (!leafletMap || !leafletMap._loaded || !leafletMap.getBounds) return null;
    // Check if map container and panes exist before trying to get bounds
    // This prevents errors when map is mounting/unmounting
    if (!leafletMap.getContainer || !leafletMap.getPane) return null;
    try {
      const container = leafletMap.getContainer();
      if (!container || !container.parentElement) return null;
      const bounds = leafletMap.getBounds();
      if (!bounds || !bounds.getSouthWest || !bounds.getNorthEast) return null;
      const sw = bounds.getSouthWest();
      const ne = bounds.getNorthEast();
      if (!sw || !ne) return null;
      return [
        [sw.lat, sw.lng],
        [ne.lat, ne.lng],
      ];
    } catch (error) {
      // Silently fail - this can happen during mount/unmount transitions
      return null;
    }
  }, []);
  
  const map = useMapEvents({
    locationfound: (e) => {
      console.log('📍 Location found via Leaflet:', e.latlng, 'accuracy:', e.accuracy);
      if (onLocationFound) {
        onLocationFound(e.latlng, e.accuracy);
      }
    },
    locationerror: (e) => {
      console.warn('📍 Location error via Leaflet:', e.message);
      if (onLocationError) {
        onLocationError(e.message);
      }
    },
    zoomend: () => {
      if (!map || !map._loaded || !map._mapPane) return;
      if (onZoomChange) {
        onZoomChange(map.getZoom());
      }
      // Also trigger viewport change on zoom
      if (onViewportChange) {
        const center = map.getCenter();
        if (center) {
          const bounds = map.getBounds();
          const radiusMeters = bounds ? map.distance(center, bounds.getNorthEast()) : 0;
          const radiusKm = Math.ceil(radiusMeters / 1000);
          onViewportChange(
            { lat: center.lat, lng: center.lng },
            map.getZoom(),
            radiusKm,
            getBoundsPayload(map)
          );
        }
      }
    },
    moveend: () => {
      // Trigger viewport change when map is moved (debounced)
      if (onViewportChange) {
        // Clear any pending timeout
        if (viewportChangeTimeoutRef.current) {
          clearTimeout(viewportChangeTimeoutRef.current);
        }
        // Debounce the callback to avoid too many updates during dragging
        viewportChangeTimeoutRef.current = setTimeout(() => {
          if (!map || !map._loaded || !map._mapPane) return;
          const center = map.getCenter();
          if (center) {
            const bounds = map.getBounds();
            const radiusMeters = bounds ? map.distance(center, bounds.getNorthEast()) : 0;
            const radiusKm = Math.ceil(radiusMeters / 1000);
            onViewportChange(
              { lat: center.lat, lng: center.lng },
              map.getZoom(),
              radiusKm,
              getBoundsPayload(map)
            );
          }
        }, 300); // 300ms debounce
      }
    },
    click: (e) => {
      if (onMapClick) {
        if (mode === 'create') {
          onMapClick(e.latlng);
        } else if (mode === 'view' || mode === 'picker') {
          // In view mode and picker mode, also call onMapClick for external handling (like MapPicker)
          onMapClick(e.latlng);
        }
      }
    },
    dragstart: () => {
      if (!ignoreUserInteraction && onUserInteracted) onUserInteracted('drag');
    },
    zoomstart: () => {
      if (!ignoreUserInteraction && onUserInteracted) onUserInteracted('zoom');
    }
  });

  // Use Leaflet's native location tracking for mobile optimization
  useEffect(() => {
    if (enableLocationTracking && map) {
      // Start location tracking with mobile-optimized settings
      map.locate({
        setView: false, // Don't auto-set view, we handle this manually
        maxZoom: 16,
        timeout: 10000, // 10 second timeout
        maximumAge: 60000, // 1 minute cache
        enableHighAccuracy: true, // High accuracy for mobile
        watch: false // One-time location, not continuous tracking
      });
    }
    // Initialize viewport with current map state if available
    if (map && !hasInitializedViewportRef.current) {
      const center = map.getCenter();
      if (center && onViewportChange) {
        const bounds = map.getBounds();
        const radiusMeters = bounds ? map.distance(center, bounds.getNorthEast()) : 0;
        const radiusKm = Math.ceil(radiusMeters / 1000);
        // Initial call to set correct radius - prevent loop by using ref
        hasInitializedViewportRef.current = true;
        // Wrap in timeout to avoid update-during-render warning
        setTimeout(() => {
           if (map && map._loaded && map.getContainer && map.getContainer().parentElement) {
             const boundsPayload = getBoundsPayload(map);
             onViewportChange(
               { lat: center.lat, lng: center.lng },
               map.getZoom(),
               radiusKm,
               boundsPayload
             );
           }
        }, 0);
      }
    }
  }, [enableLocationTracking, getBoundsPayload, map, onViewportChange]); // Only depends on map initialization or tracking toggle

  // Only jump to user location if we're not showing a specific project
  useEffect(() => {
    if (userLocation && map && autoFollow && !skipLocationUpdate) {
      try {
        const container = map.getContainer();
        if (container && container.offsetParent) {
          // Avoid forcing an arbitrary zoom; respect current zoom
          map.setView([userLocation.lat, userLocation.lng], map.getZoom());
        }
      } catch (e) {
        console.warn('Map setView in MapEventHandler failed:', e);
      }
    }
  }, [userLocation, map, skipLocationUpdate, autoFollow]);

  // Cleanup pending viewport debounce on unmount
  useEffect(() => {
    return () => {
      if (viewportChangeTimeoutRef.current) {
        clearTimeout(viewportChangeTimeoutRef.current);
      }
    };
  }, []);

  return null;
};

// Helper function to get a representative position from geolocation data
const getProjectPosition = (geolocation) => {
  return normalizeProjectGeolocation({ geolocation });
};

// Helper function to check if geolocation is a trail
const isTrail = (geolocation) => {
  return projectHasTrail({ geolocation });
};

// Normalize [lat, lng] tuples regardless of object/array input
const normalizeLatLngTuple = (value) => {
  if (!value) return null;
  if (Array.isArray(value) && value.length === 2) {
    const [lat, lng] = value;
    return Number.isFinite(lat) && Number.isFinite(lng) ? [Number(lat), Number(lng)] : null;
  }
  if (typeof value === 'object') {
    const lat = value.lat ?? value.latitude;
    const lng = value.lng ?? value.longitude ?? value.lon;
    return Number.isFinite(lat) && Number.isFinite(lng) ? [Number(lat), Number(lng)] : null;
  }
  return null;
};

// Compare map centers with small tolerance to avoid float jitter
const areCentersEqual = (a, b, tolerance = 1e-6) => {
  if (a === b) return true;
  if (!Array.isArray(a) || !Array.isArray(b) || a.length !== 2 || b.length !== 2) {
    return false;
  }
  return (
    Math.abs(Number(a[0]) - Number(b[0])) < tolerance &&
    Math.abs(Number(a[1]) - Number(b[1])) < tolerance
  );
};

// Normalize bounds to [[swLat, swLng], [neLat, neLng]]
const normalizeBounds = (value) => {
  if (!value) return null;
  if (Array.isArray(value) && value.length >= 2) {
    const sw = normalizeLatLngTuple(value[0]);
    const ne = normalizeLatLngTuple(value[1]);
    return sw && ne ? [sw, ne] : null;
  }
  if (typeof value === 'object' && value._southWest && value._northEast) {
    const sw = normalizeLatLngTuple(value._southWest);
    const ne = normalizeLatLngTuple(value._northEast);
    return sw && ne ? [sw, ne] : null;
  }
  return null;
};

const areBoundsEqual = (a, b, tolerance = 1e-6) => {
  if (a === b) return true;
  if (!a || !b) return !a && !b;
  return (
    Math.abs(a[0][0] - b[0][0]) < tolerance &&
    Math.abs(a[0][1] - b[0][1]) < tolerance &&
    Math.abs(a[1][0] - b[1][0]) < tolerance &&
    Math.abs(a[1][1] - b[1][1]) < tolerance
  );
};

// Create peer location icon helper (defined inside or outside component, but needs to be accessible)
const createPeerLocationIcon = (avatarUrl, username, heading) => {
  const initial = username ? username[0].toUpperCase() : '?';
  const hasHeading = typeof heading === 'number' && !Number.isNaN(heading);
  
  const html = `
    <div class="peer-location-marker relative transition-transform duration-300 ease-linear" style="${hasHeading ? `transform: rotate(${heading}deg);` : ''}">
      <!-- Pulsing ring -->
      <div class="absolute inset-0 rounded-full bg-blue-500/30 animate-ping"></div>
      
      <!-- Avatar/Initial -->
      <div class="relative w-8 h-8 rounded-full border-2 border-white shadow-md bg-blue-500 overflow-hidden flex items-center justify-center transform transition-transform duration-300 ${hasHeading ? 'rotate-[-' + heading + 'deg]' : ''}">
        ${avatarUrl 
          ? `<img src="${avatarUrl}" class="w-full h-full object-cover" alt="" />`
          : `<span class="text-white text-xs font-bold">${initial}</span>`
        }
      </div>
      
      <!-- Heading Arrow (if available) -->
      ${hasHeading ? `
        <div class="absolute -top-1 left-1/2 -translate-x-1/2 w-0 h-0 border-l-[4px] border-l-transparent border-r-[4px] border-r-transparent border-b-[6px] border-b-blue-600"></div>
      ` : ''}
    </div>
  `;

  return L.divIcon({
    className: 'peer-marker-container',
    html,
    iconSize: [32, 32],
    iconAnchor: [16, 16],
    popupAnchor: [0, -16]
  });
};

const UnifiedMap = ({ 
  // View mode props (from ExploreMap)
  projects = [], 
  userLocation = null, 
  onProjectSelect = null,
  onMarkerSelect = null, // Callback with full project object for preview cards
  selectedProjectId = null, // ID of currently selected project (for active marker styling)
  center = null,
  zoom = 10,
  bounds = null,
  showUserLocation = true,
  showProjectNumbers = true,
  
  // Mode control
  mode = 'view', // 'view' | 'create' | 'picker'
  
  // Map interaction props (for both modes)
  onMapClick = null, // Callback for map clicks (any mode)
  
  // Create mode props (from InteractiveMap)
  onWaypointAdd = null,
  waypoints = [],
  onWaypointsChange = null,
  
  // AI suggestions props
  aiSuggestions = [], // Array of AI-suggested locations
  mapZoomTarget = null, // { lat, lng, zoom } - Target to zoom to

  // Objective station pins (objective-driven routes)
  objectivePins = [], // [{ id, name, number, lat, lng, completed, current }]
  stationFocusRequest = null, // { id, nonce } — focus a station pin and open its popup
  
  // Message pins props
  messagePins = [], // Array of clustered message pins from chat messages
  showMessagePins = true, // Toggle to show/hide message pins
  minZoomForPins = 10, // Minimum zoom level to show pins
  onPinClick = null, // Callback when a non-cluster pin is clicked (id)
  
  // Hotspot props
  hotspots = [],
  showHotspots = true,
  onHotspotSelect = null, // Callback when a hotspot is clicked (passes full hotspot object)
  selectedHotspotId = null, // ID of currently selected hotspot (for active marker styling)
  
  // Peer locations (live multi-user tracking)
  peerLocations = {}, // { userId: { lat, lng, heading, username, avatar } }

  // Shared scrapbook: geotagged photos from all participants
  photoPins = [], // [{ id, url, lat, lng, username, takenAt }]
  
  // Layout context for proper positioning
  layoutContext = 'default', // 'explore' | 'project' | 'sidebar' | 'contained' | 'default'
  
  // Live/replay overlays
  liveTrailPoints = [], // [[lat,lng], ...]
  replayPath = [], // [[lat,lng], ...]
  replayPosition = null, // {lat,lng}
  replayAvatar = null, // User avatar URL for replay marker
  bottomOverlayPercent = 0,
  // Controls visibility of the follow/"locate me" button overlay
  showFollowButton = true,
  // When true, on mount (and when userLocation becomes available) auto-center once to user location
  autoCenterOnUser = false,
  // If true, auto-center even when explicit center/bounds are provided
  autoCenterIgnoreExplicitCenter = false,
  // Callback to provide map instance to parent
  onMapInstance = null,
  // Callback when map viewport changes (center/zoom/bounds)
  onViewportChange = null, // (center: {lat, lng}, zoom: number, radiusKm?: number, bounds?: [[lat,lng],[lat,lng]]) => void
}) => {
  // TODO: Temporarily disable waypoint creation - set to true to re-enable
  const ENABLE_WAYPOINT_CREATION = false;
  // Debug mode for compass calibration UI - set to true for admin debugging
  const DEBUG_COMPASS_UI = false;
  const [mapCenter, setMapCenter] = useState(() => {
    if (center && center.length === 2) {
      return center;
    }
    return [47.6062, -122.3321]; // Default to Seattle
  });
  
  // Static initial values for uncontrolled MapContainer to prevent snapping
  const [initialCenter] = useState(mapCenter);
  const [initialZoom] = useState(zoom);

  const [currentZoom, setCurrentZoom] = useState(zoom);
  const mapRef = useRef();
  const [shouldFitBounds, setShouldFitBounds] = useState(!!bounds);
  const mountedRef = useRef(true);
  const [autoFollow, setAutoFollow] = useState(false);
  const [mapIsReady, setMapIsReady] = useState(false);
  const lastCompassFrameRef = useRef(0);
  const locateTimeoutRef = useRef(null);
  const autoCenteredRef = useRef(false);
  const userHasInteractedRef = useRef(false);
  const prevCenterPropRef = useRef(center ? normalizeLatLngTuple(center) : null);
  const prevBoundsPropRef = useRef(normalizeBounds(bounds));

  // Debug logger: always log for diagnostics
  const debugLog = useCallback((...args) => { try { console.log('[UnifiedMap]', ...args); } catch {} }, []);

  const updateMapCenterState = useCallback((nextCenter) => {
    const normalized = normalizeLatLngTuple(nextCenter);
    if (!normalized) return;
    setMapCenter((prev) => (prev && areCentersEqual(prev, normalized) ? prev : normalized));
  }, []);

  const externalViewportChange = onViewportChange;

  const handleViewportChange = useCallback((centerPoint, zoomLevel, radius, bounds) => {
    if (centerPoint) {
      updateMapCenterState([centerPoint.lat, centerPoint.lng]);
    }
    if (typeof zoomLevel === 'number' && !Number.isNaN(zoomLevel)) {
      setCurrentZoom((prev) => (prev === zoomLevel ? prev : zoomLevel));
    }
    if (externalViewportChange) {
      externalViewportChange(centerPoint, zoomLevel, radius, bounds);
    }
  }, [externalViewportChange, updateMapCenterState]);

  const handleUserInteracted = useCallback((type) => {
    if (!userHasInteractedRef.current) {
      debugLog('User map interaction detected', type);
    }
    userHasInteractedRef.current = true;
    setAutoFollow(false);
    if (mapRef.current) {
      try {
        const center = mapRef.current.getCenter();
        if (center) {
          updateMapCenterState([center.lat, center.lng]);
        }
      } catch {}
    }
  }, [debugLog, updateMapCenterState]);

  const haptic = (duration = 10) => {
    try {
      if (typeof navigator !== 'undefined' && 'vibrate' in navigator) {
        navigator.vibrate(duration);
      }
    } catch {}
  };

  // Map rotation hooks are defined after compass state declarations

  // Create mode state
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [showAddWaypoint, setShowAddWaypoint] = useState(false);
  const [waypointName, setWaypointName] = useState('');

  // Cleanup effect to prevent operations on unmounted component
  useEffect(() => {
    // Add a small delay to ensure DOM is stable during route transitions
    const timeoutId = setTimeout(() => {
      mountedRef.current = true;
    }, 50);
    
    const currentMapRef = mapRef;
    return () => {
      clearTimeout(timeoutId);
      mountedRef.current = false;
      
      // Clear all global timeouts
      if (window.mapCleanupTimeouts) {
        window.mapCleanupTimeouts.forEach(id => clearTimeout(id));
        window.mapCleanupTimeouts.clear();
      }
      
      // Clear icon cache to prevent memory leaks
      iconCache.clear();
      
      // Cancel any pending map operations
      const currentMap = currentMapRef.current;
      if (currentMap) {
        try {
          currentMap.stopLocate?.();
          currentMap.stop?.();
          currentMap.remove?.(); // Properly destroy map instance
        } catch (e) {
          // Ignore cleanup errors
        }
      }
      // Stop geolocation watch
      try {
        if (watchIdRef.current !== null && navigator.geolocation) {
          navigator.geolocation.clearWatch(watchIdRef.current);
          watchIdRef.current = null;
        }
      } catch (e) {}
    };
  }, []);

  // Helper function to safely fit bounds
  const safeFitBounds = useCallback((map, bounds, options = {}) => {
    if (!mountedRef.current || !map) return false;
    
    try {
      // Check if map container exists and has been initialized
      const container = map.getContainer();
      if (!container || !container.offsetParent) {
        return false;
      }
      
      // Check if map has proper size
      const size = map.getSize();
      if (!size || size.x === 0 || size.y === 0) {
        return false;
      }
      
      // Additional check for Leaflet internal state
      if (!map._loaded || !map._container) {
        return false;
      }
      
      // Additional check for map pane existence
      if (!map._mapPane || !map._mapPane.style) {
        return false;
      }
      
      // Check if we're not in the middle of an animation
      if (map._animatingZoom) {
        return false;
      }

      // Asymmetric padding to account for bottom overlay (mobile chat panel)
      const basePad = 20;
      const extraBottomPx = (bottomOverlayPercent && bottomOverlayPercent > 0.01 && bottomOverlayPercent < 0.95)
        ? Math.floor(size.y * bottomOverlayPercent)
        : 0;
      const fitOpts = {
        paddingTopLeft: [basePad, basePad],
        paddingBottomRight: [basePad, basePad + extraBottomPx],
        maxZoom: 16,
        animate: false, // Disable animation to prevent async issues
        ...options
      };
      
      // Wrap in requestAnimationFrame to ensure we don't conflict with ongoing layout changes
      requestAnimationFrame(() => {
        if (mountedRef.current && map && map._loaded && !map._animatingZoom) {
          try {
             map.fitBounds(bounds, fitOpts);
          } catch (e) {
             console.warn('Deferred fitBounds failed:', e);
          }
        }
      });
      
      return true;
    } catch (e) {
      console.warn('Map fitBounds failed:', e);
      return false;
    }
  }, [bottomOverlayPercent]);

  // Helper function to safely set view
  const safeSetView = useCallback((map, center, zoom) => {
    if (!mountedRef.current || !map) return false;
    
    try {
      // Check if map container exists and has been initialized
      const container = map.getContainer();
      if (!container || !container.offsetParent) {
        return false;
      }
      
      // Additional check for map pane existence
      if (!map._mapPane || !map._mapPane.style) {
        return false;
      }
      
      // Check if we're not in the middle of an animation
      if (map._animatingZoom) {
        return false;
      }
      
      requestAnimationFrame(() => {
        if (mountedRef.current && map && map._loaded && !map._animatingZoom) {
           try {
             map.setView(center, zoom, { animate: false }); // Disable animation
           } catch(e) {
             console.warn('Deferred setView failed:', e);
           }
        }
      });

      return true;
    } catch (e) {
      console.warn('Map setView failed:', e);
      return false;
    }
  }, []);

  // Center on a latlng while compensating for a bottom overlay (mobile chat panel)
  const setViewCenteredWithOverlay = useCallback((map, latLng, zoom) => {
    if (!mountedRef.current || !map) return false;
    try {
      const container = map.getContainer();
      if (!container || !container.offsetParent) return false;
      if (!map._mapPane || !map._mapPane.style) return false;
      if (map._animatingZoom) return false;

      const size = map.getSize();
      // Clamp overlay percent to avoid extreme values from transient UI states
      const effPercent = (bottomOverlayPercent > 0.02 && bottomOverlayPercent < 0.98)
        ? Math.min(bottomOverlayPercent, 0.85)
        : 0;
      const overlayPx = effPercent > 0
        ? Math.floor((size.y * effPercent) / 2)
        : 0;

      requestAnimationFrame(() => {
        if (!mountedRef.current || !map || !map._loaded || map._animatingZoom) return;
        
        try {
          if (overlayPx > 0) {
            // Move the map center downward by overlayPx so the target appears higher (in visible center)
            const beforePt = map.latLngToContainerPoint(latLng);
            const targetPoint = beforePt.add(L.point(0, overlayPx));
            const targetLatLng = map.containerPointToLatLng(targetPoint);
            
            map.setView(targetLatLng, zoom ?? map.getZoom(), { animate: false });
          } else {
            map.setView(latLng, zoom ?? map.getZoom(), { animate: false });
          }
        } catch (e) {
           console.warn('Deferred setViewCenteredWithOverlay failed:', e);
        }
      });
      return true;
    } catch (e) {
      console.warn('Map setViewCenteredWithOverlay failed:', e);
      return false;
    }
  }, [bottomOverlayPercent]);


  // Simple clustering algorithm (for view mode)
  const clusterProjects = useCallback((projects, zoomLevel) => {
    if (mode !== 'view') return [];
    
    // Show individual pins when zoomed in enough (zoom 12+)
    if (zoomLevel >= 12) {
      return projects.filter(p => p.geolocation).map(project => {
        const position = getProjectPosition(project.geolocation);
        if (!position) return null;
        return {
          type: 'individual',
          project,
          position: [position.lat, position.lng],
          id: project.id
        };
      }).filter(Boolean);
    }

    // Group nearby projects when zoomed out
    const clusters = [];
    const processed = new Set();
    const clustered = projects.filter(p => p.geolocation).map(p => ({
      ...p,
      _position: getProjectPosition(p.geolocation)
    })).filter(p => p._position);

    clustered.forEach((project, index) => {
      if (processed.has(index)) return;

      const nearbyProjects = [project];
      const cluster = {
        type: 'cluster',
        projects: nearbyProjects,
        position: [project._position.lat, project._position.lng],
        id: `cluster-${index}`
      };

      // Find nearby projects (scale distance threshold with zoom level)
      const distanceThreshold = Math.max(0.3, 1.5 / Math.pow(2, zoomLevel - 7));
      
      clustered.forEach((otherProject, otherIndex) => {
        if (processed.has(otherIndex) || index === otherIndex) return;

        const distance = Math.sqrt(
          Math.pow(project._position.lat - otherProject._position.lat, 2) +
          Math.pow(project._position.lng - otherProject._position.lng, 2)
        );

        if (distance < distanceThreshold) {
          nearbyProjects.push(otherProject);
          processed.add(otherIndex);
        }
      });

      // Only create cluster if there are multiple projects
      if (nearbyProjects.length > 1) {
        // Calculate cluster center
        const avgLat = nearbyProjects.reduce((sum, p) => sum + p._position.lat, 0) / nearbyProjects.length;
        const avgLng = nearbyProjects.reduce((sum, p) => sum + p._position.lng, 0) / nearbyProjects.length;
        cluster.position = [avgLat, avgLng];
        cluster.projects = nearbyProjects;
        clusters.push(cluster);
      } else {
        // Single project - show as individual marker
        clusters.push({
          type: 'individual',
          project,
          position: [project._position.lat, project._position.lng],
          id: project.id
        });
      }

      processed.add(index);
    });

    return clusters;
  }, [mode]);

  // Get clustered markers based on current zoom (view mode only) - MEMOIZED for performance
  const markers = useMemo(() => {
    return mode === 'view' ? clusterProjects(projects, currentZoom) : [];
  }, [mode, projects, currentZoom, clusterProjects]);

  // Helper to track project set identity to distinguish between "new data" and "new context"
  const getProjectIdsHash = (projs) => {
    if (!projs || projs.length === 0) return '';
    return projs.map(p => p.id).sort().join(',');
  };
  const prevProjectIdsRef = useRef(projects ? getProjectIdsHash(projects) : '');

  // Fit bounds for trails or update center
  useEffect(() => {
    if (!mountedRef.current) return;
    
    let timeoutId1 = null;
    let timeoutId2 = null;

    const cleanup = () => {
      if (timeoutId1) clearTimeout(timeoutId1);
      if (timeoutId2) clearTimeout(timeoutId2);
    };

    const currentProjectIdsHash = getProjectIdsHash(projects);
    const projectsChanged = currentProjectIdsHash !== prevProjectIdsRef.current;
    prevProjectIdsRef.current = currentProjectIdsHash;

    const normalizedCenterProp = normalizeLatLngTuple(center);
    const prevCenter = prevCenterPropRef.current;
    const centerChanged = normalizedCenterProp && !areCentersEqual(normalizedCenterProp, prevCenter);
    prevCenterPropRef.current = normalizedCenterProp || null;

    const normalizedBounds = normalizeBounds(bounds);
    const prevBounds = prevBoundsPropRef.current;
    const boundsChanged = (!areBoundsEqual(normalizedBounds, prevBounds));
    
    if (boundsChanged) {
      prevBoundsPropRef.current = normalizedBounds;
      if (bounds) {
        // Only reset interaction and fit bounds if:
        // 1. Projects set context changed (e.g. selected different list/project)
        // 2. Auto-follow is explicitly enabled
        // 3. User has NEVER interacted with the map yet
        // This prevents "snapping" when background data updates bounds (like live messages/location)
        if (projectsChanged || autoFollow || !userHasInteractedRef.current) {
           if (projectsChanged) {
             userHasInteractedRef.current = false;
           }
           setShouldFitBounds(true);
        } else {
           // Debug log for suppressed snap
           debugLog('Suppressing bounds update due to user interaction', { bounds, projectsChanged, autoFollow });
        }
      }
    }
    
    if (mapRef.current && bounds && shouldFitBounds) {
      // Delay fitBounds to ensure map is fully ready
      timeoutId1 = setTimeout(() => {
        if (mountedRef.current && mapRef.current) {
          // Double check interaction state before executing delayed fitBounds
          // unless it's a forced update (projects changed or autoFollow)
          if (userHasInteractedRef.current && !autoFollow && !projectsChanged) {
             setShouldFitBounds(false);
             return;
          }
          
          const success = safeFitBounds(mapRef.current, bounds);
          if (success && mountedRef.current) {
            setShouldFitBounds(false);
          } else if (mountedRef.current) {
            // Retry with a longer delay
            timeoutId2 = setTimeout(() => {
              if (mountedRef.current && mapRef.current) {
                safeFitBounds(mapRef.current, bounds);
                if (mountedRef.current) {
                  setShouldFitBounds(false);
                }
              }
            }, 300);
          }
        }
      }, 100);
      
      return cleanup;
    }

    if (centerChanged && normalizedCenterProp) {
      userHasInteractedRef.current = false;
      updateMapCenterState(normalizedCenterProp);
      if (typeof zoom === 'number') {
        setCurrentZoom(zoom);
      }
      
      // Explicitly update view since MapContainer is now uncontrolled
      if (mapRef.current) {
        safeSetView(
          mapRef.current, 
          normalizedCenterProp, 
          typeof zoom === 'number' ? zoom : mapRef.current.getZoom()
        );
      }
      
      return cleanup;
    }

    if (userHasInteractedRef.current && !autoFollow) {
      return cleanup;
    }

    if (userLocation && !bounds) {
      updateMapCenterState([userLocation.lat, userLocation.lng]);
      // If map is ready and auto-follow enabled, apply overlay-compensated recenter
      if (mapRef.current) {
        if (autoFollow) {
          setViewCenteredWithOverlay(mapRef.current, L.latLng(userLocation.lat, userLocation.lng), mapRef.current.getZoom());
        } else if (!userHasInteractedRef.current) {
           // If user hasn't interacted, we can gently center (or just set view without overlay comp if prefered)
           // Using safeSetView to standard center if not auto-following but should update
           safeSetView(mapRef.current, [userLocation.lat, userLocation.lng], mapRef.current.getZoom());
        }
      }
      return cleanup;
    }

    if (projects.length > 0 && !bounds && mode === 'view') {
      // Center on first project with location
      const projectWithLocation = projects.find(p => p.geolocation);
      if (projectWithLocation && mountedRef.current) {
        const position = getProjectPosition(projectWithLocation.geolocation);
        if (position && mountedRef.current) {
          updateMapCenterState([position.lat, position.lng]);
          // Explicitly update view
          if (mapRef.current && !userHasInteractedRef.current) {
             safeSetView(mapRef.current, [position.lat, position.lng], mapRef.current.getZoom());
          }
        }
      }
    }

    return cleanup;
  }, [projects, userLocation, center, bounds, zoom, shouldFitBounds, mode, autoFollow, setViewCenteredWithOverlay, safeFitBounds, updateMapCenterState, safeSetView, debugLog]);

  // Explore-specific: auto center once to user location when requested
  useEffect(() => {
    if (!mountedRef.current) return;
    if (!mapIsReady) return;
    if (!autoCenterOnUser) return;
    if (!userLocation) return;
    if (!autoCenterIgnoreExplicitCenter && (bounds || center)) return; // Respect explicit bounds/center unless override
    if (!mapRef.current) return;
    if (autoCenteredRef.current) return;
    const zoomLevel = 12;
    const success = setViewCenteredWithOverlay(mapRef.current, L.latLng(userLocation.lat, userLocation.lng), zoomLevel);
    if (success) {
      userHasInteractedRef.current = false;
      updateMapCenterState([userLocation.lat, userLocation.lng]);
      autoCenteredRef.current = true;
    }
  }, [autoCenterOnUser, userLocation, bounds, center, setViewCenteredWithOverlay, mapIsReady, autoCenterIgnoreExplicitCenter, autoFollow, updateMapCenterState]);

  // Handle map zoom target (for location card clicks)
  useEffect(() => {
    if (!mapZoomTarget || !mapRef.current || !mapIsReady) return;
    const { lat, lng, zoom: targetZoom } = mapZoomTarget;
    if (typeof lat === 'number' && typeof lng === 'number' && typeof targetZoom === 'number') {
      debugLog('mapZoomTarget', { lat, lng, targetZoom, bottomOverlayPercent });
      // Center with compensation for bottom overlay so target isn't hidden behind the chat panel
      userHasInteractedRef.current = false;
      setViewCenteredWithOverlay(mapRef.current, L.latLng(lat, lng), targetZoom);
      updateMapCenterState([lat, lng]);
    }
  }, [mapZoomTarget, mapIsReady, setViewCenteredWithOverlay, bottomOverlayPercent, debugLog, updateMapCenterState]);

  // Create mode: Handle map clicks to select location
  const handleMapClick = (latlng) => {
    if (mode !== 'create' && mode !== 'picker') return;
    if (mode === 'create' && !ENABLE_WAYPOINT_CREATION) return;
    setSelectedLocation([latlng.lat, latlng.lng]);
    setShowAddWaypoint(true);
    setWaypointName('');
  };

  // Create mode: Add waypoint with name
  const addWaypoint = () => {
    if (selectedLocation && waypointName.trim()) {
      const newWaypoint = {
        id: Date.now(),
        position: selectedLocation,
        name: waypointName.trim(),
        timestamp: new Date().toISOString(),
      };
      
      const newWaypoints = [...waypoints, newWaypoint];
      
      if (onWaypointAdd) {
        onWaypointAdd(newWaypoint);
      }
      
      if (onWaypointsChange) {
        onWaypointsChange(newWaypoints);
      }
      
      setShowAddWaypoint(false);
      setSelectedLocation(null);
      setWaypointName('');
    }
  };

  // Create mode: Cancel waypoint addition
  const cancelAddWaypoint = () => {
    setShowAddWaypoint(false);
    setSelectedLocation(null);
    setWaypointName('');
  };

  // State for location tracking
  const [isLocating, setIsLocating] = useState(false);
  // Geolocation error is logged non-intrusively; no state needed
  const [userAccuracy, setUserAccuracy] = useState(null);
  const [internalUserLocation, setInternalUserLocation] = useState(null);
  const watchIdRef = useRef(null);
  const lastUpdateRef = useRef(0);
  const shouldRecenterOnNextFixRef = useRef(false);
  const hasGeoHeadingRef = useRef(false);

  // State for compass/direction tracking
  const [compassHeading, setCompassHeading] = useState(null);
  const [compassSupported, setCompassSupported] = useState(false);
  const [compassPermissionStatus, setCompassPermissionStatus] = useState('unknown');
  const compassLastUpdateRef = useRef(0);
  const compassTargetRef = useRef(null);
  const compassAnimationIdRef = useRef(null);
  const [compassCalibrationMode, setCompassCalibrationMode] = useState(0); // 0=auto, 1=inverted, 2=raw
  const [compassImmediateMode, setCompassImmediateMode] = useState(false); // true=no animation
  const [compassReversed, setCompassReversed] = useState(false); // true=reverse direction
  const [magneticDeclination, setMagneticDeclination] = useState(0); // magnetic declination offset in degrees
  const [useGimbalLockFix, setUseGimbalLockFix] = useState(true); // apply gimbal lock compensation
  
  // Use shared compass hook for raw event data (avoids duplicate listeners)
  const { rawEvent: sharedRawEvent, isSupported: sharedCompassSupported, permissionStatus: sharedPermissionStatus } = useCompassRaw();

  // No heading-based recentering; auto-follow simply keeps the user centered (with overlay compensation)

  // Compute distance between two lat/lng points (meters)
  const computeDistanceMeters = (a, b) => {
    if (!a || !b) return Infinity;
    const toRad = (x) => (x * Math.PI) / 180;
    const R = 6371000; // meters
    const dLat = toRad(b.lat - a.lat);
    const dLng = toRad(b.lng - a.lng);
    const lat1 = toRad(a.lat);
    const lat2 = toRad(b.lat);
    const sinDLat = Math.sin(dLat / 2);
    const sinDLng = Math.sin(dLng / 2);
    const aVal = sinDLat * sinDLat + Math.cos(lat1) * Math.cos(lat2) * sinDLng * sinDLng;
    const c = 2 * Math.atan2(Math.sqrt(aVal), Math.sqrt(1 - aVal));
    return R * c;
  };

  // Calculate magnetic declination based on location (approximate)
  const calculateMagneticDeclination = useCallback((lat, lng) => {
    // Simplified magnetic declination calculation
    // This is an approximation - real apps would use NOAA's magnetic field model
    
    // Major magnetic declination zones (approximate)
    if (!lat || !lng) return 0;
    
    // North America approximate values
    if (lat > 30 && lat < 70 && lng > -170 && lng < -50) {
      // Eastern US: positive declination (magnetic north is east of true north)
      if (lng > -85) return Math.max(0, 20 - (lng + 70) * 0.3);
      // Western US: negative to positive
      else return Math.max(-20, (lng + 120) * 0.4);
    }
    
    // Europe approximate values
    if (lat > 40 && lat < 70 && lng > -10 && lng < 40) {
      return Math.max(-5, 5 - lat * 0.1);
    }
    
    // Default: no correction
    return 0;
  }, []);

  // Advanced compass heading calculation. Input is the W3C alpha (COUNTER-
  // clockwise around Z); gimbal-lock compensation runs in that alpha space,
  // then the result is converted to a clockwise-from-north compass heading
  // (compass = 360 − alpha — using alpha directly mirrors east/west, the
  // long-standing "north is flipped vs Google Maps" bug), then declination
  // and smoothing are applied.
  const calculateCompassHeading = useCallback((alpha, beta, gamma, lat, lng) => {
    let correctedAlpha = alpha;

    // Apply gimbal lock compensation if enabled and device is tilted
    if (useGimbalLockFix && beta !== null && gamma !== null) {
      const betaRad = (beta * Math.PI) / 180;
      const gammaRad = (gamma * Math.PI) / 180;
      const alphaRad = (alpha * Math.PI) / 180;
      
      // Check if device is significantly tilted (gimbal lock territory)
      const tiltMagnitude = Math.sqrt(beta * beta + gamma * gamma);
      
      if (tiltMagnitude > 30) { // Device tilted more than 30 degrees
        // Apply coordinate system transformation to compensate for tilt
        // This uses rotation matrix math to project compass onto horizontal plane
        const sinB = Math.sin(betaRad);
        const cosB = Math.cos(betaRad);
        const sinG = Math.sin(gammaRad);
        const cosG = Math.cos(gammaRad);
        const sinA = Math.sin(alphaRad);
        const cosA = Math.cos(alphaRad);
        
        // Correct rotation matrix for tilt compensation (ZXY rotation order)
        // Project magnetic north vector onto horizontal plane
        const mx = cosA * cosG - sinA * sinB * sinG;
        const my = sinA * cosB;
        // const mz = cosA * sinG + sinA * sinB * cosG;  // Z-component not needed for 2D heading
        
        // Calculate heading from horizontal projection (still alpha space)
        correctedAlpha = Math.atan2(my, mx) * (180 / Math.PI);
        if (correctedAlpha < 0) correctedAlpha += 360;
        
        console.log(`📍 Gimbal lock compensation applied: tilt=${tiltMagnitude.toFixed(1)}° original=${alpha.toFixed(1)}° corrected=${correctedAlpha.toFixed(1)}°`);
      }
    }

    // CCW device alpha → CW compass heading.
    let heading = (360 - (((correctedAlpha % 360) + 360) % 360)) % 360;
    
    // Apply magnetic declination correction if location is available
    if (lat && lng && magneticDeclination === 0) {
      // Auto-calculate declination if not manually set
      const autoDecl = calculateMagneticDeclination(lat, lng);
      if (autoDecl !== 0) {
        heading = (heading + autoDecl + 360) % 360;
        console.log(`📍 Auto magnetic declination applied: ${autoDecl.toFixed(1)}°`);
      }
    } else if (magneticDeclination !== 0) {
      // Apply manual declination setting
      heading = (heading + magneticDeclination + 360) % 360;
    }
    
    // Low-pass smoothing for heading changes
    try {
      const last = compassTargetRef.current;
      if (typeof last === 'number') {
        const diff = ((heading - last + 540) % 360) - 180; // shortest path
        const alphaS = 0.2; // smoothing factor
        heading = (last + diff * alphaS + 360) % 360;
      }
    } catch {}
    return heading;
  }, [useGimbalLockFix, magneticDeclination, calculateMagneticDeclination]);

  // Mobile optimization hooks
  const { isMobile } = useMobile();
  const { touchSupported } = useTouch();
  const mapRenderer = useMemo(() => (isMobile ? L.canvas() : undefined), [isMobile]);
  // Only disable wheel/double-click zoom when we’re truly on a touch-capable mobile device.
  // This keeps desktop “mobile emulation” usable with a mouse/trackpad.
  const disableWheelZoom = isMobile && touchSupported;
  // Default zoom when enabling auto-follow so surroundings are visible
  const defaultFollowZoom = useMemo(() => (isMobile ? 15 : 14), [isMobile]);

  // Update internal user location from GeolocationPosition with throttling
  const updateUserFromPosition = useCallback((position) => {
    if (!mountedRef.current) return;
    const { latitude, longitude, accuracy, heading } = position.coords || {};
    if (latitude == null || longitude == null) return;

    const now = Date.now();
    const minIntervalMs = 1000; // throttle to ~1Hz
    const nextCandidate = { lat: latitude, lng: longitude };
    const movedMeters = computeDistanceMeters(internalUserLocation, nextCandidate);
    const minMoveMeters = 3; // ignore tiny jitter
    if (now - lastUpdateRef.current < minIntervalMs && movedMeters < minMoveMeters) {
      return;
    }

    lastUpdateRef.current = now;
    setInternalUserLocation(nextCandidate);
    setUserAccuracy(typeof accuracy === 'number' ? accuracy : null);

    if (typeof heading === 'number' && !Number.isNaN(heading) && heading >= 0) {
      // Geolocation heading: 0° = North, clockwise
      const normalized = ((heading % 360) + 360) % 360;
      // Only use geolocation heading if compass is not available or unreliable
      if (!compassSupported || compassHeading === null) {
        hasGeoHeadingRef.current = true;
        setCompassHeading(normalized);
      }
    }

    if (shouldRecenterOnNextFixRef.current && mapRef.current) {
      // On first fix after enabling follow, use a sensible default zoom
      setViewCenteredWithOverlay(mapRef.current, L.latLng(latitude, longitude), defaultFollowZoom);
      updateMapCenterState([latitude, longitude]);
      shouldRecenterOnNextFixRef.current = false;
    } else if (autoFollow && mapRef.current) {
      setViewCenteredWithOverlay(mapRef.current, L.latLng(latitude, longitude), mapRef.current.getZoom());
      updateMapCenterState([latitude, longitude]);
    }
  }, [internalUserLocation, autoFollow, setViewCenteredWithOverlay, compassSupported, compassHeading, defaultFollowZoom, updateMapCenterState]);

  // Start continuous geolocation watch
  const startContinuousTracking = useCallback(() => {
    if (!navigator.geolocation || !LocationConsentService.canAccessLocation()) {
      return;
    }

    try {
      // Clear any existing watch
      if (watchIdRef.current !== null) {
        navigator.geolocation.clearWatch(watchIdRef.current);
        watchIdRef.current = null;
      }

      setIsLocating(true);
      shouldRecenterOnNextFixRef.current = true;

      const id = navigator.geolocation.watchPosition(
        (pos) => {
          updateUserFromPosition(pos);
          setIsLocating(false);
        },
        (err) => {
          setIsLocating(false);
          console.warn('Live geolocation watch error:', err?.message || err);
        },
        {
          enableHighAccuracy: true,
          maximumAge: 10000,  // Reduced from 60000 for better accuracy
          timeout: 10000,      // Reduced from 15000 to fail faster
        }
      );
      watchIdRef.current = id;
    } catch (e) {
      setIsLocating(false);
      console.error('Failed to start geolocation watch:', e);
    }
  }, [updateUserFromPosition]);


  // (moved isMobile/touchSupported/defaultFollowZoom above for order)

  // Handle location found via Leaflet's native locate
  const handleLocationFound = (latlng, accuracy) => {
    if (!mountedRef.current) return;
    setIsLocating(false);
    setUserAccuracy(accuracy);
    
    if (mapRef.current && mountedRef.current) {
      // Use sensible default follow zoom for better context
      setViewCenteredWithOverlay(mapRef.current, L.latLng(latlng.lat, latlng.lng), defaultFollowZoom);
      updateMapCenterState([latlng.lat, latlng.lng]);
    }
  };

  // Handle location error via Leaflet's native locate
  const handleLocationError = (message) => {
    if (!mountedRef.current) return;
    setIsLocating(false);
    try { if (locateTimeoutRef.current) clearTimeout(locateTimeoutRef.current); } catch {}
    // Quiet down intrusive alerts; log once
    console.warn('📍 Geolocation error (suppressed alert):', message);
    // Turn follow off when we cannot get location on mobile
    setAutoFollow(false);
  };

  // (moved isMobile/touchSupported/defaultFollowZoom above for order)

  // Improved browser detection for compass calibration
  const isAndroidStock = useMemo(() => {
    const userAgent = navigator.userAgent;
    return /Android/.test(userAgent) && !/Chrome/.test(userAgent) && /WebKit/.test(userAgent);
  }, []);

  const isChrome = useMemo(() => {
    return window.chrome !== undefined;
  }, []);

  const isIOS = useMemo(() => {
    const userAgent = navigator.userAgent;
    return /iPad|iPhone|iPod/.test(userAgent) && !window.MSStream;
  }, []);

  const isSafari = useMemo(() => {
    const userAgent = navigator.userAgent;
    return /Safari/.test(userAgent) && !/Chrome/.test(userAgent);
  }, []);

  // Reset compass to use device orientation instead of geolocation
  const resetToCompassMode = useCallback(() => {
    hasGeoHeadingRef.current = false;
    compassTargetRef.current = null;
    if (compassAnimationIdRef.current) {
      cancelAnimationFrame(compassAnimationIdRef.current);
      compassAnimationIdRef.current = null;
    }
    console.log('📍 Reset to compass mode - geolocation heading override disabled');
  }, []);

  // Optimized compass animation function
  const startCompassAnimation = useCallback(() => {
    if (compassAnimationIdRef.current) return; // Already running
    
    const animate = (ts) => {
      if (!mountedRef.current) {
        compassAnimationIdRef.current = null;
        return;
      }
      
      const target = compassTargetRef.current;
      if (typeof target === 'number') {
        const current = typeof compassHeading === 'number' ? compassHeading : target;
        
        // Calculate shortest angular difference
        let diff = ((target - current + 540) % 360) - 180;
        
        // Stop animation if we're very close to target
        if (Math.abs(diff) < 0.5) {
          setCompassHeading(target);
          compassAnimationIdRef.current = null;
          return;
        }
        
        // More responsive easing with frame rate limiting
        const last = lastCompassFrameRef.current || 0;
        if (!last || ts - last >= 16) { // ~60fps limit
          // Much more responsive easing factors
          const easingFactor = Math.abs(diff) > 30 ? 0.6 : Math.abs(diff) > 10 ? 0.4 : 0.25;
          const next = current + diff * easingFactor;
          setCompassHeading(((next % 360) + 360) % 360);
          lastCompassFrameRef.current = ts;
        }
      }
      
      compassAnimationIdRef.current = requestAnimationFrame(animate);
    };
    
    compassAnimationIdRef.current = requestAnimationFrame(animate);
  }, [compassHeading]);

  // Cleanup compass animation on unmount
  useEffect(() => {
    return () => {
      if (compassAnimationIdRef.current) {
        cancelAnimationFrame(compassAnimationIdRef.current);
        compassAnimationIdRef.current = null;
      }
    };
  }, []);

  // Compass/Device Orientation handling - now uses shared compass hook to avoid duplicate listeners
  useEffect(() => {
    // Update supported/permission status from shared hook
    setCompassSupported(sharedCompassSupported);
    setCompassPermissionStatus(sharedPermissionStatus);

    // Process raw events from shared compass hook (avoids duplicate listeners)
    if (!sharedRawEvent || !mountedRef.current) return;

    // Throttle orientation events to reduce CPU usage (limit to ~20Hz for smoother updates)
    const now = Date.now();
    if (now - compassLastUpdateRef.current < 50) return;  // 20Hz max

    const event = sharedRawEvent;
    
    // Get all orientation values for advanced processing
    const alpha = event.alpha;
    const beta = event.beta;
    const gamma = event.gamma;
    let heading = null;
    
    if (event.webkitCompassHeading !== undefined && event.webkitCompassHeading !== null) {
      // iOS Safari: already clockwise from north — compensate UI rotation only.
      heading = (event.webkitCompassHeading + getScreenAngle()) % 360;
    } else if (alpha !== null && alpha !== undefined && typeof alpha === 'number' && isFinite(alpha)) {
      // Only ABSOLUTE orientation events are north-referenced. A relative
      // alpha is zeroed at page load (common in WebViews/Capacitor without
      // deviceorientationabsolute) — publishing it as a compass was one of
      // the "app compass differs from browser" causes. Bail and let the
      // geolocation heading (movement-based) take over instead.
      const isAbsoluteEvent =
        event.absolute === true || 'ondeviceorientationabsolute' in window;
      if (!isAbsoluteEvent) {
        heading = null;
      } else {
      const processedAlpha = ((alpha % 360) + 360) % 360;

      if (compassCalibrationMode === 0) {
        // Auto (spec-correct): gimbal compensation in alpha space, CCW→CW
        // conversion, declination, smoothing — one formula for every
        // browser. The old per-browser guesses (direct alpha on Chrome,
        // +90 on stock) were the mirrored-east/west bug.
        const currentLocation = internalUserLocation;
        heading = calculateCompassHeading(
          processedAlpha,
          beta,
          gamma,
          currentLocation?.lat,
          currentLocation?.lng
        );
        heading = (heading + getScreenAngle()) % 360;
      } else if (compassCalibrationMode === 1) {
        // Mode 1: flip of Auto — debug aid for devices that genuinely
        // report clockwise alpha (rare non-conformant sensors).
        const currentLocation = internalUserLocation;
        heading = calculateCompassHeading(
          processedAlpha,
          beta,
          gamma,
          currentLocation?.lat,
          currentLocation?.lng
        );
        heading = (360 - ((heading + getScreenAngle()) % 360)) % 360;
      } else {
        // Mode 2: raw alpha, no corrections (debug).
        heading = processedAlpha;
      }

      // Apply direction reversal if needed (manual user toggle)
      if (compassReversed && heading !== null) {
        heading = (360 - heading + 360) % 360;
      }
      }
      
      // Debug logging to understand device behavior
      if (heading !== null && Date.now() - compassLastUpdateRef.current > 1000) {
        const browserType = isIOS ? (isSafari ? 'Safari' : 'iOS-Other') : isChrome ? 'Chrome' : isAndroidStock ? 'Stock' : 'Unknown';
        const eventType = 'ondeviceorientationabsolute' in window ? 'absolute' : 'standard';
        const direction = compassReversed ? ' (Reversed)' : '';
        const tilt = beta && gamma ? Math.sqrt(beta * beta + gamma * gamma).toFixed(1) : 'N/A';
        console.log(`📍 Mode ${compassCalibrationMode} (${browserType}/${eventType}): alpha=${alpha.toFixed(1)}° tilt=${tilt}° → compass=${heading.toFixed(1)}°${direction}`);
      }
      
      // Additional smoothing for rapid changes (common on Android)
      const lastTarget = compassTargetRef.current;
      if (heading !== null && lastTarget !== null && typeof lastTarget === 'number') {
        const diff = Math.abs(((heading - lastTarget + 540) % 360) - 180);
        // If change is very large (>90°), it might be a calibration glitch
        if (diff > 90 && Date.now() - compassLastUpdateRef.current < 500) {
          console.warn('📍 Large compass change detected, ignoring potential glitch:', diff);
          return; // Skip this update
        }
      }
    }
    
    if (heading !== null && !Number.isNaN(heading) && heading >= 0 && heading <= 360) {
      // Normalize heading to 0-360 range
      const normalized = ((heading % 360) + 360) % 360;
      
      // Only update if we don't have an active geolocation heading override
      if (!hasGeoHeadingRef.current) {
        compassLastUpdateRef.current = now;
        
        if (compassImmediateMode) {
          // Immediate mode: set heading directly without animation
          setCompassHeading(normalized);
        } else {
          // Animated mode: update target and start animation
          compassTargetRef.current = normalized;
          if (!compassAnimationIdRef.current) {
            startCompassAnimation();
          }
        }
      }
    }
  }, [sharedRawEvent, sharedCompassSupported, sharedPermissionStatus, resetToCompassMode, startCompassAnimation, compassCalibrationMode, compassImmediateMode, compassReversed, magneticDeclination, useGimbalLockFix, isChrome, isAndroidStock, isIOS, isSafari, calculateCompassHeading, internalUserLocation]);

  // Function to request compass permission (for user-triggered requests)
  // Note: This now triggers the shared compass manager to re-initialize
  const requestCompassPermission = async () => {
    if (typeof DeviceOrientationEvent.requestPermission === 'function') {
      try {
        // Request permission and let the shared hook handle re-initialization
        const permission = await DeviceOrientationEvent.requestPermission();
        setCompassPermissionStatus(permission);
        
        if (permission === 'granted') {
          setCompassSupported(true);
          resetToCompassMode(); // Switch to compass mode when permission granted
          // The shared compass hook will pick up the permission change on next event
          return true;
        }
      } catch (error) {
        console.error('📍 Error requesting compass permission:', error);
        setCompassPermissionStatus('denied');
      }
    }
    return false;
  };

  // Debounced resize handler for mobile performance
  const debouncedResize = useCallback(() => {
    if (!mountedRef.current || !mapRef.current) return;
    
    const timeoutId = setTimeout(() => {
      if (mountedRef.current && mapRef.current) {
        try {
          mapRef.current.invalidateSize();
          // After a resize/orientation change, if following, re-apply overlay-compensated centering
          if (autoFollow && (internalUserLocation || userLocation)) {
            const loc = internalUserLocation || userLocation;
            setViewCenteredWithOverlay(mapRef.current, L.latLng(loc.lat, loc.lng), mapRef.current.getZoom());
            updateMapCenterState([loc.lat, loc.lng]);
          }
        } catch (e) {
          console.warn('Map resize failed:', e);
        }
      }
      // Clean up from the set after execution
      if (window.mapCleanupTimeouts) {
        window.mapCleanupTimeouts.delete(timeoutId);
      }
    }, 100);
    
    // Store timeout for cleanup
    if (!window.mapCleanupTimeouts) window.mapCleanupTimeouts = new Set();
    window.mapCleanupTimeouts.add(timeoutId);
  }, [autoFollow, internalUserLocation, userLocation, setViewCenteredWithOverlay, updateMapCenterState]);

  // Handle orientation changes on mobile
  useEffect(() => {
    if (!isMobile) return;

    const handleOrientationChange = () => {
      debouncedResize();
    };

    window.addEventListener('orientationchange', handleOrientationChange);
    return () => window.removeEventListener('orientationchange', handleOrientationChange);
  }, [isMobile, debouncedResize]);

  // Note: Do NOT auto-start tracking; only start on explicit follow toggle to avoid mobile GPS flapping

  // Re-center when bottom overlay height changes so user stays visible
  useEffect(() => {
    if (!mountedRef.current) return;
    if (!mapIsReady) return;
    if (!autoFollow) return;
    const loc = internalUserLocation || userLocation;
    if (!loc) return;
    if (!mapRef.current) return;
    try { mapRef.current.invalidateSize(); } catch {}
    setViewCenteredWithOverlay(mapRef.current, L.latLng(loc.lat, loc.lng), mapRef.current.getZoom());
    updateMapCenterState([loc.lat, loc.lng]);
  }, [bottomOverlayPercent, autoFollow, mapIsReady, internalUserLocation, userLocation, setViewCenteredWithOverlay, updateMapCenterState]);

  // Invalidate size on window resize
  useEffect(() => {
    const onResize = () => {
      if (!mountedRef.current || !mapRef.current) return;
      try { mapRef.current.invalidateSize(); } catch {}
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);


  // My Location button: start continuous tracking and recentre on first fix
  const getCurrentLocation = useCallback(() => {
    startContinuousTracking();
  }, [startContinuousTracking]);

  // Format duration for display
  const formatDuration = (minutes) => {
    if (!minutes) return '';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) {
      return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
    }
    return `${mins}m`;
  };

  // Format distance for display
  const formatDistance = (distance) => {
    if (!distance) return '';
    return distance < 1 ? `${(distance * 1000).toFixed(0)}m` : `${distance.toFixed(1)}km`;
  };

  // Create trail path from waypoints (create mode and picker mode)
  const trailPath = (mode === 'create' || mode === 'picker') ? waypoints.map(waypoint => waypoint.position) : [];

  // --- Objective station navigation (deterministic "let's go there" layer) ---
  const currentStationPin = useMemo(
    () => objectivePins.find((pin) => pin.current) || null,
    [objectivePins]
  );
  // Camera move to a station: fit user + station when the user is close enough
  // for both to be meaningful; otherwise center the station (virtual tour).
  const focusStation = useCallback((pin) => {
    if (!pin || !mapRef.current) return;
    const loc = internalUserLocation || userLocation;
    userHasInteractedRef.current = false;
    let dist = null;
    if (loc) {
      try {
        dist = L.latLng(loc.lat, loc.lng).distanceTo(L.latLng(pin.lat, pin.lng));
      } catch {
        dist = null;
      }
    }
    if (loc && dist !== null && dist < 20000) {
      safeFitBounds(
        mapRef.current,
        L.latLngBounds([[loc.lat, loc.lng], [pin.lat, pin.lng]]),
        { maxZoom: 17, animate: true }
      );
    } else {
      setViewCenteredWithOverlay(mapRef.current, L.latLng(pin.lat, pin.lng), 16);
      updateMapCenterState([pin.lat, pin.lng]);
    }
  }, [internalUserLocation, userLocation, safeFitBounds, setViewCenteredWithOverlay, updateMapCenterState]);

  // Smart recenter: once per station change (when the guide's focus moves),
  // then leave the camera alone.
  const prevStationIdRef = useRef(null);
  useEffect(() => {
    const id = currentStationPin?.id || null;
    if (id === prevStationIdRef.current) return;
    prevStationIdRef.current = id;
    if (!currentStationPin || !mapIsReady) return;
    focusStation(currentStationPin);
  }, [currentStationPin, mapIsReady, focusStation]);

  // Explicit focus request from the chat ("Show on map" chip): recenter on the
  // station AND open its popup — the popup carries the Route link, so Google
  // Maps stays one tap away without being the primary move.
  const objectiveMarkerRefs = useRef({});
  const lastStationFocusNonceRef = useRef(null);
  useEffect(() => {
    if (!stationFocusRequest?.id || !mapIsReady) return;
    if (stationFocusRequest.nonce === lastStationFocusNonceRef.current) return;
    const pin = objectivePins.find((p) => p.id === stationFocusRequest.id);
    if (!pin) return;
    lastStationFocusNonceRef.current = stationFocusRequest.nonce;
    focusStation(pin);
    // Open the popup after the camera move settles (fly/fit animates ~400ms)
    const timer = setTimeout(() => {
      try {
        objectiveMarkerRefs.current[pin.id]?.openPopup();
      } catch (_) {}
    }, 450);
    return () => clearTimeout(timer);
  }, [stationFocusRequest, mapIsReady, objectivePins, focusStation]);

  return (
    <div className="h-full flex flex-col bg-white relative">
      {/* Map Container */}
      <div className="flex-1 relative overflow-hidden touch-manipulation">
        <MapContainer
          center={initialCenter}
          zoom={initialZoom}
          style={{ height: '100%', width: '100%' }}
          ref={mapRef}
          className="focus:outline-none touch-manipulation no-tap-highlight"
          zoomControl={false}
          scrollWheelZoom={!disableWheelZoom} // Permit wheel zoom in desktop emulation
          touchZoom={true}
          doubleClickZoom={!disableWheelZoom} // Match wheel behavior for consistency
          dragging={true}
          // Enhanced mobile optimizations
          tap={touchSupported}
          tapTolerance={isMobile ? 25 : 15} // Larger tolerance on mobile
          touchExtend={isMobile ? 2 : 1} // More responsive touch on mobile
          bounceAtZoomLimits={false}
          // Mobile performance optimizations
          preferCanvas={isMobile} // Use canvas on mobile for better performance
          renderer={mapRenderer}
          // Mobile-friendly popup settings
          closePopupOnClick={false}
          maxBounds={undefined}
          // Better mobile interaction
          keyboard={false}
          // Mobile zoom limits - Allow much broader zoom out
          minZoom={3}
          maxZoom={isMobile ? 20 : 21}  // Increased for more detail (tiles upscale beyond zoom 19)
          // Mobile attribution control
          attributionControl={!isMobile}
          // Additional performance optimizations
          wheelDebounceTime={60}
          zoomSnap={0.5}
          zoomAnimation={true}
          fadeAnimation={true}
          markerZoomAnimation={true}
        whenReady={(e) => {
            setMapIsReady(true);
            // Capture the actual Leaflet map instance from event
            try { if (e && e.target) { mapRef.current = e.target; } } catch {}
            
            
            // Provide map instance to parent component
            if (onMapInstance && mapRef.current) {
              onMapInstance(mapRef.current);
            }
            // Prioritize auto-centering when override is enabled
            if (autoCenterOnUser && userLocation && mapRef.current && mountedRef.current && (autoCenterIgnoreExplicitCenter || (!center && !bounds))) {
              safeSetView(mapRef.current, [userLocation.lat, userLocation.lng], 12);
              userHasInteractedRef.current = false;
              updateMapCenterState([userLocation.lat, userLocation.lng]);
              autoCenteredRef.current = true;
              return;
            }
            if (bounds && mapRef.current && mountedRef.current) {
              const timeoutId = setTimeout(() => {
                if (mapRef.current && mountedRef.current) {
                  safeFitBounds(mapRef.current, bounds);
                }
                // Clean up from the set after execution
                if (window.mapCleanupTimeouts) {
                  window.mapCleanupTimeouts.delete(timeoutId);
                }
              }, isMobile ? 300 : 150); // Longer delay on mobile
              
              // Store timeout for cleanup
              if (!window.mapCleanupTimeouts) window.mapCleanupTimeouts = new Set();
              window.mapCleanupTimeouts.add(timeoutId);
            }
          }}
        >

          {/* ESRI “World Imagery”*/}
          <TileLayer
            attribution=''
            url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
            minZoom={0}
            maxZoom={22}
            maxNativeZoom={22}
            errorTileUrl="data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7"
            updateWhenIdle={false}
            updateWhenZooming={true}
            keepBuffer={isMobile ? 1 : 2}
          />

          {/* Map Tile Options - swap to change style */}
          {/* attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors' */}
          {/* 
          <TileLayer
            attribution=''
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            minZoom={0}
            maxZoom={21}  // Allow zooming beyond native tiles (upscales for more detail)
            maxNativeZoom={19}  // OSM tiles only go to zoom 19, will upscale beyond
            subdomains={['a', 'b', 'c']}
            errorTileUrl="data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7"
            updateWhenIdle={isMobile}
            updateWhenZooming={!isMobile}
            keepBuffer={isMobile ? 1 : 2}
          /> */}

          {/* MapTiler “OSM Bright”*/}
          {/* <TileLayer
            attribution=''
            url="https://api.maptiler.com/maps/outdoor-v2/{z}/{x}/{y}.png?key=O5x2Cev0mqezbWpeYb3G"
            minZoom={0}
            maxZoom={20}
            maxNativeZoom={20}
            subdomains={['a', 'b', 'c']}
            errorTileUrl="data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7"
            updateWhenIdle={isMobile}
            updateWhenZooming={!isMobile}
            keepBuffer={isMobile ? 1 : 2}
          /> */}
          
          {/* CartoDB Voyager (colorful, good for exploration) */}
          {/* <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
            url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png"
            minZoom={0}
            maxZoom={19}
            maxNativeZoom={19}
            subdomains={['a', 'b', 'c', 'd']}
            errorTileUrl="data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7"
            updateWhenIdle={isMobile}
            updateWhenZooming={!isMobile}
            keepBuffer={isMobile ? 1 : 2}
          /> */}
          
          {/* Other options to try: */}
          {/* CartoDB Positron (light, minimal) */}
          {/* <TileLayer
            attribution='&copy; OpenStreetMap &copy; CARTO'
            url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
          /> */}
          
          {/* CartoDB Dark Matter (dark mode) */}
          {/* <TileLayer
            attribution='&copy; OpenStreetMap &copy; CARTO'
            url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
          /> */}

          {/* Map event handler */}
      <MapEventHandler 
            userLocation={userLocation} 
            onLocationFound={handleLocationFound}
            onLocationError={handleLocationError}
            onViewportChange={handleViewportChange}
            onZoomChange={setCurrentZoom}
            skipLocationUpdate={!!bounds || !!center}
            mode={mode}
            onMapClick={onMapClick || (mode === 'picker' ? handleMapClick : null)}
            enableLocationTracking={false} // We use navigator.geolocation.watchPosition for continuous updates
            autoFollow={autoFollow}
        onUserInteracted={handleUserInteracted}
        ignoreUserInteraction={isLocating}
          />
          
          {/* Peer location markers (live multiplayer) */}
          {Object.entries(peerLocations).map(([peerId, loc]) => (
            loc && loc.lat && loc.lng ? (
              <Marker
                key={`peer-${peerId}`}
                position={[loc.lat, loc.lng]}
                icon={createPeerLocationIcon(loc.avatar, loc.username, loc.heading)}
                zIndexOffset={900} // Below user but above map elements
              >
                <Popup className="unified-map-popup" closeButton={false} autoPan={false}>
                  <div className="px-3 py-2 text-center">
                    <div className="font-semibold text-sm text-gray-900 dark:text-white">
                      {loc.username || 'Participant'}
                    </div>
                    {loc.speed > 0 && (
                      <div className="text-xs text-gray-500">
                        {Math.round(loc.speed * 3.6)} km/h
                      </div>
                    )}
                  </div>
                </Popup>
              </Marker>
            ) : null
          ))}

          {/* Shared scrapbook: geotagged photos from all participants */}
          {(photoPins || []).map((pin) => {
            const src = pin?.url ? getImageUrl(pin.url) : null;
            if (!src || !Number.isFinite(pin.lat) || !Number.isFinite(pin.lng)) return null;
            return (
              <Marker
                key={`photo-${pin.id}`}
                position={[pin.lat, pin.lng]}
                icon={createPhotoPinIcon(src, 36, '#ffffff')}
                zIndexOffset={800} // Below peers/user, above stations
              >
                <Popup className="unified-map-popup" closeButton={false} autoPan={false}>
                  <div className="w-44">
                    <img
                      src={src}
                      alt={pin.username ? `Photo by ${pin.username}` : 'Photo'}
                      className="w-full h-32 object-cover rounded-lg"
                      loading="lazy"
                    />
                    <div className="px-1 pt-1.5 pb-0.5 flex items-center justify-between gap-2">
                      <span className="text-xs font-semibold text-gray-900 dark:text-white truncate">
                        {pin.username || 'Photo'}
                      </span>
                      {pin.takenAt && (
                        <span className="text-[10px] text-gray-500 flex-shrink-0">
                          {new Date(pin.takenAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </span>
                      )}
                    </div>
                  </div>
                </Popup>
              </Marker>
            );
          })}

          {/* User location marker with accuracy circle (live-tracked) */}
          {showUserLocation && (internalUserLocation || userLocation) && (
            <>
              <Marker 
                position={[
                  (internalUserLocation || userLocation).lat,
                  (internalUserLocation || userLocation).lng
                ]}
                icon={createUserLocationWithCompassIcon(
                  typeof compassHeading === 'number' ? compassHeading : null
                )}
              >
                <Popup className="unified-map-popup">
                  <div className="min-w-[200px] -m-3">
                    {/* Header with gradient accent */}
                    <div className="relative bg-gradient-to-r from-blue-500 to-blue-600 px-4 py-3 rounded-t-xl">
                      <div className="flex items-center gap-2.5">
                        <div className="w-8 h-8 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center">
                          <Navigation className="w-4 h-4 text-white" />
                        </div>
                        <div>
                          <div className="font-semibold text-white text-sm">Your Location</div>
                          {userAccuracy && (
                            <div className="text-[11px] text-white/80">±{Math.round(userAccuracy)}m accuracy</div>
                          )}
                        </div>
                      </div>
                    </div>
                    {/* Coordinates */}
                    <div className="px-4 py-3 bg-white dark:bg-gray-800">
                      <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400 font-mono">
                        <span className="text-gray-400 dark:text-gray-500">📍</span>
                        {(internalUserLocation || userLocation).lat.toFixed(5)}, {(internalUserLocation || userLocation).lng.toFixed(5)}
                      </div>
                    </div>
                    {/* Compass information - Debug UI for calibration (guarded) */}
                    {DEBUG_COMPASS_UI && isMobile && (
                      <div className="px-4 py-2 border-t border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 rounded-b-xl">
                        {compassSupported && compassHeading !== null ? (
                          <div className="text-xs text-purple-600 dark:text-purple-400">
                            🧭 Facing: {Math.round(compassHeading)}° 
                            {(() => {
                              const bearing = (compassHeading + 360) % 360;
                              if (bearing < 22.5 || bearing >= 337.5) return ' (N)';
                              if (bearing < 67.5) return ' (NE)';
                              if (bearing < 112.5) return ' (E)';
                              if (bearing < 157.5) return ' (SE)';
                              if (bearing < 202.5) return ' (S)';
                              if (bearing < 247.5) return ' (SW)';
                              if (bearing < 292.5) return ' (W)';
                              return ' (NW)';
                            })()}
                            <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                              {hasGeoHeadingRef.current ? 'GPS' : 'Device'} • 
                              {compassAnimationIdRef.current ? ' Animating' : ' Static'}
                              <div className="mt-1 text-xs">
                                Browser: {isIOS ? (isSafari ? 'Safari' : 'iOS Other') : isChrome ? 'Chrome' : isAndroidStock ? 'Stock' : 'Unknown'} • 
                                Event: {'ondeviceorientationabsolute' in window ? 'Absolute' : 'Standard'}
                                {isIOS && (
                                  <div className="text-purple-600 dark:text-purple-400">
                                    🍎 iOS: Uses webkitCompassHeading when available
                                  </div>
                                )}
                              </div>
                              <div className="mt-1">
                                Calibration: 
                                <button 
                                  onClick={() => setCompassCalibrationMode(prev => (prev + 1) % 3)}
                                  className="ml-1 px-2 py-1 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded text-xs"
                                >
                                  Mode {compassCalibrationMode} {compassCalibrationMode === 0 ? '(Auto)' : compassCalibrationMode === 1 ? '(Inverted)' : '(Raw)'}
                                </button>
                              </div>
                              <div className="mt-1">
                                Animation: 
                                <button 
                                  onClick={() => setCompassImmediateMode(prev => !prev)}
                                  className={`ml-1 px-2 py-1 rounded text-xs ${compassImmediateMode ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300' : 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'}`}
                                >
                                  {compassImmediateMode ? 'Immediate' : 'Smooth'}
                                </button>
                              </div>
                              <div className="mt-1">
                                Direction: 
                                <button 
                                  onClick={() => setCompassReversed(prev => !prev)}
                                  className={`ml-1 px-2 py-1 rounded text-xs ${compassReversed ? 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300' : 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'}`}
                                >
                                  {compassReversed ? 'Reversed' : 'Normal'}
                                </button>
                              </div>
                              <div className="mt-1">
                                Mag Decl: 
                                <button 
                                  onClick={() => setMagneticDeclination(prev => prev - 5)}
                                  className="ml-1 px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-l text-xs"
                                >
                                  -5°
                                </button>
                                <span className="px-2 py-1 bg-gray-200 dark:bg-gray-600 text-gray-800 dark:text-gray-200 text-xs">
                                  {magneticDeclination.toFixed(0)}°
                                </span>
                                <button 
                                  onClick={() => setMagneticDeclination(prev => prev + 5)}
                                  className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-r text-xs"
                                >
                                  +5°
                                </button>
                                <button 
                                  onClick={() => setMagneticDeclination(0)}
                                  className="ml-1 px-2 py-1 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded text-xs"
                                >
                                  Reset
                                </button>
                              </div>
                              <div className="mt-1">
                                Tilt Fix: 
                                <button 
                                  onClick={() => setUseGimbalLockFix(prev => !prev)}
                                  className={`ml-1 px-2 py-1 rounded text-xs ${useGimbalLockFix ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300' : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'}`}
                                >
                                  {useGimbalLockFix ? 'Enabled' : 'Disabled'}
                                </button>
                              </div>
                            </div>
                            {Date.now() - compassLastUpdateRef.current > 5000 && (
                              <div className="text-xs text-red-500 dark:text-red-400 mt-1">
                                ⚠ Compass stuck (no updates for {Math.round((Date.now() - compassLastUpdateRef.current) / 1000)}s)
                                <button 
                                  onClick={resetToCompassMode}
                                  className="ml-2 underline"
                                >
                                  Reset
                                </button>
                              </div>
                            )}
                          </div>
                        ) : compassPermissionStatus === 'denied' ? (
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            🧭 Compass access denied
                          </div>
                        ) : compassPermissionStatus === 'unknown' && typeof DeviceOrientationEvent.requestPermission === 'function' ? (
                          <button 
                            onClick={requestCompassPermission}
                            className="text-xs text-blue-600 dark:text-blue-400 underline"
                          >
                            Enable Compass Direction
                          </button>
                        ) : !compassSupported ? (
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            🧭 Compass not available
                          </div>
                        ) : (
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            🧭 Waiting for compass...
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </Popup>
              </Marker>
              
              {/* True accuracy circle in meters */}
              {userAccuracy && userAccuracy < 5000 && (
                <Circle
                  center={[
                    (internalUserLocation || userLocation).lat,
                    (internalUserLocation || userLocation).lng
                  ]}
                  radius={Math.max(5, Math.min(userAccuracy, 1000))}
                  pathOptions={{ color: '#3B82F6', fillColor: '#3B82F6', fillOpacity: 0.1 }}
                />
              )}
            </>
          )}
          
          {/* VIEW MODE: Project trail polylines */}
          {mode === 'view' && projects.filter(p => isTrail(p.geolocation)).map((project) => (
            <Polyline
              key={`trail-${project.id}`}
              positions={getProjectTrailPoints(project).map(point => [point.lat, point.lng])}
              color='#003fff'
              weight={3}
              opacity={0.7}
            />
          ))}
          
          {/* VIEW MODE: Clustered project markers */}
          {mode === 'view' && markers.map((marker) => {
            if (marker.type === 'individual') {
              // Individual project marker
              const project = marker.project;
              const isActive = selectedProjectId === project.id;
              return (
                <Marker
                  key={marker.id}
                  position={marker.position}
                  icon={createProjectMarkerIcon(isActive)}
                  zIndexOffset={isActive ? 1000 : 0}
                  eventHandlers={{
                    click: () => {
                      // If onMarkerSelect is provided, call it with project (for bottom card)
                      if (onMarkerSelect) {
                        onMarkerSelect(project);
                      }
                    }
                  }}
                >
                  {/* Only show popup if onMarkerSelect is not provided (fallback) */}
                  {!onMarkerSelect && (
                  <Popup className="unified-map-popup">
                    <div className="min-w-[280px] max-w-[320px] -m-3 overflow-hidden rounded-xl bg-white dark:bg-gray-800">
                      {/* Project Image with overlay gradient */}
                      {project.images && project.images.length > 0 ? (
                        <div className="relative h-36">
                          <img
                            src={getImageUrl(project.images[0], 'cardmobile')}
                            alt={project.name}
                            className="w-full h-full object-cover"
                          />
                          {/* Gradient overlay for text readability */}
                          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                          {/* Badges on image */}
                          <div className="absolute top-2.5 right-2.5 flex items-center gap-1.5">
                            {project.rating && project.rating.average > 0 && (
                              <div className="flex items-center gap-1 px-2 py-1 bg-black/50 backdrop-blur-sm rounded-full">
                                <Star className="w-3 h-3 text-yellow-400 fill-current" />
                                <span className="text-xs font-medium text-white">{project.rating.average.toFixed(1)}</span>
                              </div>
                            )}
                          </div>
                          {/* Title on image */}
                          <div className="absolute bottom-0 left-0 right-0 p-3">
                            <h3 className="font-semibold text-white text-[15px] leading-tight line-clamp-2 drop-shadow-sm">
                              {project.name}
                            </h3>
                          </div>
                        </div>
                      ) : (
                        <div className="relative h-28 bg-gradient-to-br from-emerald-50 to-sky-50 dark:from-emerald-900/30 dark:to-sky-900/30 flex items-center justify-center">
                          <span className="text-4xl opacity-30">🌲</span>
                          <div className="absolute bottom-0 left-0 right-0 p-3">
                            <h3 className="font-semibold text-gray-900 dark:text-white text-[15px] leading-tight line-clamp-2">
                              {project.name}
                            </h3>
                          </div>
                        </div>
                      )}
                      
                      {/* Project Meta */}
                      <div className="p-3 space-y-2.5">
                        {/* Tags row */}
                        <div className="flex flex-wrap items-center gap-1.5">
                          {/* {isTrail(project.geolocation) && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-medium bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 ring-1 ring-blue-100 dark:ring-blue-800/50">
                              🥾 Trail
                            </span>
                          )} */}
                          {project.difficulty && (
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-medium ring-1 ${
                              project.difficulty === 'easy' 
                                ? 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 ring-emerald-100 dark:ring-emerald-800/50' 
                                : project.difficulty === 'hard' 
                                  ? 'bg-rose-50 dark:bg-rose-900/30 text-rose-700 dark:text-rose-300 ring-rose-100 dark:ring-rose-800/50'
                                  : 'bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 ring-amber-100 dark:ring-amber-800/50'
                            }`}>
                              {project.difficulty.charAt(0).toUpperCase() + project.difficulty.slice(1)}
                            </span>
                          )}
                        </div>

                        {/* Stats row - compact */}
                        <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                          {project.trailLength && (
                            <>
                              <div className="flex items-center gap-1">
                                <ArrowLeftRight className="w-3.5 h-3.5" />
                                <span>{project.trailLength.value}{project.trailLength.unit}</span>
                              </div>
                              <span className="text-gray-300 dark:text-gray-600">·</span>
                            </>
                          )}
                          {project.estimatedDuration && (
                            <>
                              <div className="flex items-center gap-1">
                                <Clock className="w-3.5 h-3.5" />
                                <span>{formatDuration(project.estimatedDuration)}</span>
                              </div>
                              {project.distance && <span className="text-gray-300 dark:text-gray-600">·</span>}
                            </>
                          )}
                          {project.distance && (
                            <div className="flex items-center gap-1 text-blue-600 dark:text-blue-400 font-medium">
                              <Navigation className="w-3.5 h-3.5" />
                              <span>{formatDistance(project.distance)}</span>
                            </div>
                          )}
                        </div>

                        {/* View Details Button - only show when handler is provided */}
                        {onProjectSelect && (
                          <button
                            onClick={() => onProjectSelect(project.id)}
                            className="w-full bg-gray-900 dark:bg-white text-white dark:text-gray-900 px-3 py-2 rounded-lg text-sm font-medium hover:bg-gray-800 dark:hover:bg-gray-100 transition-colors flex items-center justify-center gap-1.5"
                          >
                            View Details
                            <ArrowLeftRight className="w-3.5 h-3.5 rotate-180" />
                          </button>
                        )}
                      </div>
                    </div>
                  </Popup>
                  )}
                </Marker>
              );
            } else {
              // Cluster marker
              const clusterSize = marker.projects.length;
              const averageDifficulty = marker.projects.reduce((acc, p) => {
                const difficultyScores = { easy: 1, moderate: 2, hard: 3 };
                return acc + (difficultyScores[p.difficulty] || 2);
              }, 0) / clusterSize;
              
              const difficulty = averageDifficulty <= 1.5 ? 'easy' : averageDifficulty <= 2.5 ? 'moderate' : 'hard';
              
              return (
                <Marker
                  key={marker.id}
                  position={marker.position}
                  icon={createNumberedIcon(clusterSize, difficulty)}
                  eventHandlers={{
                    click: () => {
                      // Zoom in when cluster is clicked (just 1 level at a time)
                      if (mapRef.current && mountedRef.current) {
                        safeSetView(mapRef.current, marker.position, Math.min(currentZoom + 1, 15));
                        updateMapCenterState(marker.position);
                      }
                    }
                  }}
                >
                  <Popup className="unified-map-popup">
                    <div className="min-w-[260px] max-w-[300px] -m-3 overflow-hidden rounded-xl bg-white dark:bg-gray-800">
                      {/* Header */}
                      <div className="px-4 py-3 bg-gray-50 dark:bg-gray-900/50 border-b border-gray-100 dark:border-gray-700">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full bg-gray-900 dark:bg-white flex items-center justify-center">
                            <span className="text-sm font-bold text-white dark:text-gray-900">{clusterSize}</span>
                          </div>
                          <div>
                            <h3 className="font-semibold text-gray-900 dark:text-white text-sm">
                              {clusterSize} {clusterSize === 1 ? 'project' : 'projects'} here
                            </h3>
                            <p className="text-[11px] text-gray-500 dark:text-gray-400">Zoom in to see individual markers</p>
                          </div>
                        </div>
                      </div>
                      
                      {/* Project list */}
                      <div className="max-h-48 overflow-y-auto mobile-scroll">
                        {marker.projects.slice(0, 5).map((project, idx) => (
                          <div 
                            key={project.id}
                            className={`flex items-center gap-3 px-4 py-2.5 transition-colors ${
                              idx !== 0 ? 'border-t border-gray-100 dark:border-gray-700' : ''
                            } ${onProjectSelect ? 'hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer' : ''}`}
                            onClick={() => onProjectSelect && onProjectSelect(project.id)}
                          >
                            {/* Thumbnail or placeholder */}
                            <div className="w-10 h-10 rounded-lg bg-gray-100 dark:bg-gray-700 overflow-hidden flex-shrink-0">
                              {project.images?.[0] ? (
                                <img 
                                  src={getImageUrl(project.images[0], 'cardmobile')} 
                                  alt="" 
                                  className="w-full h-full object-cover"
                                />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center text-gray-400">
                                  <span className="text-lg">🌲</span>
                                </div>
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="font-medium text-sm text-gray-900 dark:text-white truncate">{project.name}</div>
                              {project.difficulty && (
                                <div className={`text-[11px] font-medium ${
                                  project.difficulty === 'easy' 
                                    ? 'text-emerald-600 dark:text-emerald-400' 
                                    : project.difficulty === 'hard' 
                                      ? 'text-rose-600 dark:text-rose-400'
                                      : 'text-amber-600 dark:text-amber-400'
                                }`}>
                                  {project.difficulty.charAt(0).toUpperCase() + project.difficulty.slice(1)}
                                </div>
                              )}
                            </div>
                            <ArrowLeftRight className="w-4 h-4 text-gray-400 rotate-180 flex-shrink-0" />
                          </div>
                        ))}
                        {clusterSize > 5 && (
                          <div className="px-4 py-2.5 text-xs text-gray-500 dark:text-gray-400 text-center border-t border-gray-100 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-900/30">
                            +{clusterSize - 5} more
                          </div>
                        )}
                      </div>
                      
                      {/* Zoom action */}
                      <div className="p-3 border-t border-gray-100 dark:border-gray-700">
                        <button
                          onClick={() => {
                            if (mapRef.current && mountedRef.current) {
                              safeSetView(mapRef.current, marker.position, Math.min(currentZoom + 1, 15));
                              updateMapCenterState(marker.position);
                            }
                          }}
                          className="w-full bg-gray-900 dark:bg-white text-white dark:text-gray-900 px-3 py-2 rounded-lg text-sm font-medium hover:bg-gray-800 dark:hover:bg-gray-100 transition-colors flex items-center justify-center gap-1.5"
                        >
                          <Locate className="w-4 h-4" />
                          Zoom In
                        </button>
                      </div>
                    </div>
                  </Popup>
                </Marker>
              );
            }
          })}
        
          {/* CREATE MODE: Selected location marker (temporary) */}
          {(mode === 'create' || mode === 'picker') && selectedLocation && (
            <Marker position={selectedLocation}>
              <Popup className="unified-map-popup">
                <div className="min-w-[180px] -m-3 overflow-hidden rounded-xl bg-white dark:bg-gray-800">
                  <div className="p-4 text-center">
                    <div className="w-10 h-10 mx-auto mb-2 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                      <span className="text-lg">📌</span>
                    </div>
                    <div className="font-semibold text-sm text-gray-900 dark:text-white">Selected Location</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Tap "Add Waypoint" to save
                    </div>
                  </div>
                </div>
              </Popup>
            </Marker>
          )}
          
          {/* CREATE MODE: Waypoint markers */}
          {(mode === 'create' || mode === 'picker') && waypoints.map((waypoint, index) => (
            <Marker
              key={waypoint.id}
              position={waypoint.position}
              icon={waypointIcon}
            >
              <Popup className="unified-map-popup">
                <div className="min-w-[200px] -m-3 overflow-hidden rounded-xl bg-white dark:bg-gray-800">
                  {/* Header */}
                  <div className="relative bg-gradient-to-r from-violet-500 to-purple-600 px-4 py-3">
                    <div className="flex items-center gap-2.5">
                      <div className="w-7 h-7 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center">
                        <span className="text-sm font-bold text-white">{index + 1}</span>
                      </div>
                      <div className="font-semibold text-white text-sm truncate">{waypoint.name}</div>
                    </div>
                  </div>
                  {/* Details */}
                  <div className="px-4 py-3 space-y-2">
                    <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                      <Clock className="w-3.5 h-3.5" />
                      <span>{new Date(waypoint.timestamp).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                    </div>
                    <div className="text-[11px] text-gray-400 dark:text-gray-500 font-mono">
                      📍 {waypoint.position[0].toFixed(5)}, {waypoint.position[1].toFixed(5)}
                    </div>
                  </div>
                </div>
              </Popup>
            </Marker>
          ))}
          
          {/* CREATE MODE: Trail path */}
          {(mode === 'create' || mode === 'picker') && trailPath.length > 1 && (
            <Polyline
              positions={trailPath}
              color="#003fff"
              weight={3}
              opacity={0.7}
              dashArray="5, 10"
            />
          )}

          {/* OBJECTIVE STATIONS: numbered route pins for objective-driven guides */}
          {objectivePins.map((pin) => (
            <Marker
              key={`objective-pin-${pin.id}`}
              ref={(el) => {
                if (el) objectiveMarkerRefs.current[pin.id] = el;
                else delete objectiveMarkerRefs.current[pin.id];
              }}
              position={[pin.lat, pin.lng]}
              icon={createObjectivePinIcon(pin)}
              zIndexOffset={pin.current ? 900 : 400}
            >
              <Tooltip direction="top" offset={[0, -16]} className="objective-station-tooltip">
                <span>
                  {pin.completed ? '✓ ' : `${pin.number}. `}
                  {pin.name}
                </span>
              </Tooltip>
              {/* Tap-friendly: hover tooltips don't exist on touch */}
              <Popup className="unified-map-popup" autoPan={true}>
                {/* unified-map-popup strips the wrapper bg — content brings its own */}
                <div className="px-3 py-2.5 min-w-[190px] rounded-xl bg-white dark:bg-gray-800">
                  <div className="flex items-center gap-2">
                    <span
                      className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-bold ${
                        pin.completed ? 'bg-emerald-500' : pin.current ? 'bg-indigo-500' : 'bg-gray-800'
                      }`}
                    >
                      {pin.completed ? '✓' : pin.number}
                    </span>
                    <span className="font-semibold text-sm text-gray-900 dark:text-white leading-tight">
                      {pin.name}
                    </span>
                  </div>
                  <div className="mt-2 flex items-center justify-between gap-2">
                    <span className={`text-xs font-medium ${pin.completed ? 'text-emerald-600' : 'text-gray-500 dark:text-gray-400'}`}>
                      {pin.completed ? 'Completed' : `Station ${pin.number}`}
                    </span>
                    {!pin.completed && (
                      <a
                        href={`https://maps.google.com/?q=${pin.lat},${pin.lng}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-indigo-600 text-white text-xs font-semibold hover:bg-indigo-700"
                      >
                        <Navigation className="w-3 h-3 text-white" />
                        <span className="text-white">Route</span>
                      </a>
                    )}
                  </div>
                </div>
              </Popup>
            </Marker>
          ))}

          {/* AI SUGGESTIONS: Location suggestions from AI */}
          {aiSuggestions.filter(suggestion => suggestion.coordinates).map((suggestion) => (
            <Marker
              key={suggestion.id}
              position={[suggestion.coordinates.lat, suggestion.coordinates.lng]}
              icon={createAISuggestionIcon(suggestion.confidence)}
            >
              <Popup className="unified-map-popup">
                <div className="min-w-[260px] max-w-[300px] -m-3 overflow-hidden rounded-xl bg-white dark:bg-gray-800">
                  {/* Header with gradient */}
                  <div className="relative bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 px-4 py-3">
                    <div className="absolute top-0 right-0 w-20 h-20 bg-white/10 rounded-bl-full -mr-4 -mt-4" />
                    <div className="flex items-center gap-2.5 relative">
                      <div className="w-8 h-8 bg-white/20 backdrop-blur-sm rounded-lg flex items-center justify-center">
                        <span className="text-base">🎯</span>
                      </div>
                      <div>
                        <div className="text-[11px] font-medium text-white/80">AI Suggestion</div>
                        <div className="font-semibold text-white text-sm">{suggestion.name}</div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Content */}
                  <div className="p-3 space-y-3">
                    {/* Description */}
                    {suggestion.description && (
                      <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
                        {suggestion.description}
                      </p>
                    )}
                    
                    {/* Meta row */}
                    <div className="flex items-center justify-between text-xs">
                      {/* Distance */}
                      <div className="flex items-center gap-1.5 text-gray-500 dark:text-gray-400">
                        <Navigation className="w-3.5 h-3.5 text-blue-500" />
                        <span>{suggestion.distanceText} away</span>
                      </div>
                      
                      {/* Confidence indicator */}
                      <div className="flex items-center gap-1.5">
                        <div className={`h-1.5 w-12 rounded-full overflow-hidden bg-gray-200 dark:bg-gray-700`}>
                          <div 
                            className={`h-full rounded-full ${
                              suggestion.confidence > 0.8 ? 'bg-emerald-500' : 
                              suggestion.confidence > 0.6 ? 'bg-blue-500' : 'bg-amber-500'
                            }`}
                            style={{ width: `${suggestion.confidence * 100}%` }}
                          />
                        </div>
                        <span className="text-gray-400 dark:text-gray-500">{(suggestion.confidence * 100).toFixed(0)}%</span>
                      </div>
                    </div>
                    
                    {/* Coordinates */}
                    <div className="pt-2 border-t border-gray-100 dark:border-gray-700">
                      <div className="text-[11px] text-gray-400 dark:text-gray-500 font-mono">
                        📍 {suggestion.coordinates.lat.toFixed(5)}, {suggestion.coordinates.lng.toFixed(5)}
                      </div>
                    </div>
                  </div>
                </div>
              </Popup>
            </Marker>
          ))}

          {/* MESSAGE PINS: Pins from chat messages with location data */}
          <MessagePinErrorBoundary>
            {showMessagePins && currentZoom >= minZoomForPins && messagePins.map((cluster, idx) => {
              try {
                const isCluster = cluster.type === 'cluster' && cluster.count > 1;
                const position = [cluster.location.lat, cluster.location.lng];
                
                // Validate position
                if (!position || !Array.isArray(position) || position.length !== 2 ||
                    typeof position[0] !== 'number' || typeof position[1] !== 'number' ||
                    isNaN(position[0]) || isNaN(position[1])) {
                  console.warn('Invalid position for message pin:', cluster);
                  return null;
                }
                
                const icon = isCluster 
                  ? createMessageClusterIcon(cluster)
                  : createMessagePinIcon(cluster.centerPin);

                return (
                  <Marker
                    key={cluster.id || idx}
                    position={position}
                    icon={icon}
                    eventHandlers={isCluster ? { click: () => {
                      if (mapRef.current && mountedRef.current) {
                        safeSetView(mapRef.current, position, Math.min(currentZoom + 1, 16));
                        updateMapCenterState(position);
                      }
                    } } : (onPinClick ? { click: () => { try { onPinClick(cluster.tid || cluster.id, cluster); } catch {} } } : undefined)}
                  >
                    <Popup>
                      <MessagePinPopup 
                        pin={cluster.centerPin}
                        cluster={isCluster ? cluster : null}
                        onViewDetails={onPinClick}
                      />
                    </Popup>
                  </Marker>
                );
              } catch (error) {
                console.warn('Error rendering message pin:', error, cluster);
                return null;
              }
            })}
          </MessagePinErrorBoundary>
          
          {/* DISCOVERY PINS: Items from world model inventory */}
          {showHotspots && hotspots.map((spot, idx) => {
            try {
              if (typeof spot?.lat !== 'number' || typeof spot?.lng !== 'number' || isNaN(spot.lat) || isNaN(spot.lng)) {
                return null;
              }
              
              const isCluster = (spot.count || 1) > 1;
              const hasImage = Boolean(spot.imageUrl);
              const displayName = spot.name || spot.label || 'Discovery';
              const isSelected = selectedHotspotId === spot.id;
              
              const handleDiscoveryClick = () => {
                if (spot._cluster && mapRef.current && mountedRef.current) {
                  const targetZoom = Math.min((currentZoom || 10) + 1.5, 16);
                  safeSetView(mapRef.current, [spot.lat, spot.lng], targetZoom);
                  return;
                }
                if (onHotspotSelect) {
                  onHotspotSelect(spot);
                }
              };
              
              return (
                <Marker
                  key={spot.id || `discovery-${idx}`}
                  position={[spot.lat, spot.lng]}
                  icon={createDiscoveryIcon(spot, isSelected)}
                  zIndexOffset={isSelected ? 1000 : 0}
                  eventHandlers={{
                    click: handleDiscoveryClick
                  }}
                >
                  {/* Only show popup if no onHotspotSelect handler is provided */}
                  {!onHotspotSelect && (
                  <Popup className="unified-map-popup">
                    <div className="min-w-[240px] max-w-[300px] -m-3 overflow-hidden rounded-xl bg-white dark:bg-gray-800">
                      {/* Header Image */}
                      {hasImage ? (
                        <div className="relative h-36">
                          <img 
                            src={getImageUrl(spot.imageUrl)}
                            alt={displayName}
                            className="w-full h-full object-cover"
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                          {/* Gradient overlay */}
                          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                          {/* Count badge */}
                          {isCluster && (
                            <div className="absolute top-2.5 right-2.5 px-2 py-1 bg-emerald-500 text-white text-[11px] font-bold rounded-full shadow-lg">
                              {spot.count} discoveries
                            </div>
                          )}
                          {/* Title on image */}
                          <div className="absolute bottom-0 left-0 right-0 p-3">
                            <div className="font-semibold text-white text-sm leading-tight drop-shadow-sm line-clamp-2">
                              {displayName}
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="relative px-4 py-4 bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20">
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex items-center gap-2.5">
                              <div className="w-10 h-10 rounded-xl bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
                                <span className="text-lg">🌿</span>
                              </div>
                              <div className="font-semibold text-gray-900 dark:text-white text-sm leading-tight">
                                {displayName}
                              </div>
                            </div>
                            {isCluster && (
                              <span className="shrink-0 px-2 py-0.5 bg-emerald-500 text-white text-[11px] font-bold rounded-full">
                                {spot.count}
                              </span>
                            )}
                          </div>
                        </div>
                      )}
                      
                      {/* Discovery Details */}
                      <div className="p-3 space-y-2.5">
                        {spot.locationNote && (
                          <p className="text-xs text-gray-600 dark:text-gray-300 leading-relaxed flex items-start gap-1.5">
                            <span className="text-gray-400 mt-0.5">📍</span>
                            <span>{spot.locationNote}</span>
                          </p>
                        )}
                        
                        {/* Meta row */}
                        <div className="flex items-center justify-between text-xs">
                          {/* Discoverer info */}
                          {spot.discoverers && spot.discoverers.length > 0 && (
                            <div className="flex items-center gap-1.5 text-gray-500 dark:text-gray-400">
                              <span>Found by</span>
                              <span className="font-medium text-gray-700 dark:text-gray-300">
                                {spot.discoverers.slice(0, 2).map(u => `@${u}`).join(', ')}
                                {spot.discoverers.length > 2 && ` +${spot.discoverers.length - 2}`}
                              </span>
                            </div>
                          )}
                          
                          {/* Timestamp */}
                          {spot.timestamp && (
                            <div className="flex items-center gap-1 text-gray-400 dark:text-gray-500">
                              <Clock className="w-3 h-3" />
                              {new Date(spot.timestamp).toLocaleDateString(undefined, { 
                                month: 'short', 
                                day: 'numeric',
                                year: new Date(spot.timestamp).getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined
                              })}
                            </div>
                          )}
                        </div>
                        
                        {/* Multi-item preview for clusters */}
                        {isCluster && spot.allImages && spot.allImages.length > 1 && (
                          <div className="pt-2.5 border-t border-gray-100 dark:border-gray-700">
                            <div className="text-[11px] text-gray-500 dark:text-gray-400 mb-2">More at this location</div>
                            <div className="flex gap-1.5">
                              {spot.allImages.slice(1, 4).map((img, i) => (
                                <div key={i} className="w-12 h-12 rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-700 ring-1 ring-gray-200 dark:ring-gray-600">
                                  <img 
                                    src={getImageUrl(img)} 
                                    alt="" 
                                    className="w-full h-full object-cover"
                                    onError={(e) => { e.target.parentNode.style.display = 'none'; }}
                                  />
                                </div>
                              ))}
                              {spot.allImages.length > 4 && (
                                <div className="w-12 h-12 rounded-lg bg-gray-100 dark:bg-gray-700 ring-1 ring-gray-200 dark:ring-gray-600 flex items-center justify-center text-xs text-gray-500 dark:text-gray-400 font-medium">
                                  +{spot.allImages.length - 4}
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </Popup>
                  )}
                </Marker>
              );
            } catch (error) {
              console.warn('Error rendering discovery pin:', error, spot);
              return null;
            }
          })}
          
          {/* LIVE TRAIL: draw live user trail while ongoing */}
          {Array.isArray(liveTrailPoints) && liveTrailPoints.length > 1 && (
            <Polyline
              positions={liveTrailPoints}
              color="#003fff"
              weight={4}
              opacity={0.8}
            />
          )}
          
          {/* REPLAY TRAIL: draw replay path */}
          {Array.isArray(replayPath) && replayPath.length > 1 && (
            <Polyline
              positions={replayPath}
              color="#003fff"
              weight={4}
              opacity={0.7}
            />
          )}
          
          {/* REPLAY MARKER: current replay position with avatar */}
          {replayPosition && (
            <Marker 
              position={[replayPosition.lat, replayPosition.lng]}
              {...(replayAvatar ? { icon: L.divIcon({
                html: `
                  <div style="
                    width: 32px; 
                    height: 32px; 
                    border-radius: 50%; 
                    border: 3px solid #ffffff; 
                    box-shadow: 0 2px 8px rgba(0,0,0,0.3);
                    overflow: hidden;
                    background: #ffffff;
                  ">
                    <img 
                      src="${getImageUrl(replayAvatar)}" 
                      alt="Replay position" 
                      style="
                        width: 100%; 
                        height: 100%; 
                        object-fit: cover;
                      "
                      onerror="this.style.display='none'; this.parentElement.innerHTML='<div style=\\"width:100%;height:100%;background:#4f46e5;display:flex;align-items:center;justify-content:center;color:white;font-size:12px;font-weight:bold\\">U</div>'"
                    />
                  </div>
                `,
                className: 'replay-avatar-marker',
                iconSize: [32, 32],
                iconAnchor: [16, 16]
              }) } : {})}
            >
              <Popup>Replay position</Popup>
            </Marker>
          )}
        </MapContainer>
      </div>

      {/* Follow toggle */}
      {showFollowButton && (
      <button
        onClick={() => {
          const next = !autoFollow;
          setAutoFollow(next);
          if (next) {
            userHasInteractedRef.current = false;
            try { if (locateTimeoutRef.current) clearTimeout(locateTimeoutRef.current); } catch {}
            setIsLocating(true);
            getCurrentLocation();
            // iOS compass permission prompt on gesture
            try {
              if (typeof DeviceOrientationEvent !== 'undefined' && typeof DeviceOrientationEvent.requestPermission === 'function') {
                DeviceOrientationEvent.requestPermission().then((p) => setCompassPermissionStatus(p)).catch(() => setCompassPermissionStatus('denied'));
              }
            } catch {}
            // Fallback: clear locating state after 6s if no fix
            locateTimeoutRef.current = setTimeout(() => setIsLocating(false), 6000);
          } else {
            try { if (locateTimeoutRef.current) clearTimeout(locateTimeoutRef.current); } catch {}
            setIsLocating(false);
          }
          haptic(8);
        }}
        disabled={isLocating}
        aria-label="Toggle auto-follow"
        className={`${
          layoutContext === 'contained' 
            ? 'absolute top-4 left-4' // Always absolute positioning inside contained maps
            : 'fixed md:absolute left-1 md:left-4'
        } flex items-center justify-center rounded-full shadow transition-all duration-200 ${autoFollow ? 'bg-blue-600 text-white' : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300'} border ${autoFollow ? 'border-blue-600' : 'border-gray-300 dark:border-gray-600'} hover:opacity-90 active:scale-95 w-11 h-11 ${
          layoutContext === 'contained'
            ? '' // No additional top positioning needed for contained maps
            : layoutContext === 'explore' 
              ? 'top-32 md:top-2' // More space for search bar and filters on explore page
              : 'top-32 md:top-20'  // Standard positioning for other pages
        }`}
        style={{
          zIndex: 1000,
        }}
        title={isLocating ? 'Getting your location...' : autoFollow ? 'Auto-follow ON' : 'Auto-follow OFF'}
      >
        {autoFollow ? (
          <LocateFixed className={`${isMobile ? 'w-5 h-5' : 'w-5 h-5'} ${isLocating ? 'animate-pulse' : ''}`} />
        ) : (
          <Locate className={`${isMobile ? 'w-5 h-5' : 'w-5 h-5'} ${isLocating ? 'animate-pulse' : ''}`} />
        )}
      </button>
      )}

      {/* CREATE MODE: Add Waypoint Modal - Responsive */}
      {((mode === 'create' && showAddWaypoint && ENABLE_WAYPOINT_CREATION) || (mode === 'picker' && showAddWaypoint)) && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center z-[9999999]">
          <div className="bg-white dark:bg-gray-800 w-full sm:w-auto sm:min-w-[400px] sm:max-w-md rounded-t-2xl sm:rounded-2xl shadow-2xl overflow-hidden">
            {/* Header with accent */}
            <div className="relative bg-gradient-to-r from-violet-500 to-purple-600 px-5 py-4">
              <div className="absolute top-0 right-0 w-24 h-24 bg-white/10 rounded-bl-full -mr-6 -mt-6" />
              <div className="flex items-center gap-3 relative">
                <div className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
                  <span className="text-lg">📍</span>
                </div>
                <h3 className="text-lg font-semibold text-white">Add Waypoint</h3>
              </div>
            </div>
            
            {/* Content */}
            <div className="p-5 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Waypoint Name
                </label>
                <input
                  type="text"
                  value={waypointName}
                  onChange={(e) => setWaypointName(e.target.value)}
                  placeholder="e.g., Trail Start, Viewpoint, Rest Area"
                  className="w-full border border-gray-200 dark:border-gray-600 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500"
                  autoFocus={!isMobile}
                  onKeyPress={(e) => e.key === 'Enter' && addWaypoint()}
                />
              </div>
              
              {selectedLocation && (
                <div className="flex items-center gap-2.5 text-sm text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-700/50 px-4 py-3 rounded-xl">
                  <span className="text-gray-400">📍</span>
                  <span className="font-mono text-xs">{selectedLocation[0].toFixed(5)}, {selectedLocation[1].toFixed(5)}</span>
                </div>
              )}
              
              <div className="flex gap-3 pt-1">
                <button
                  onClick={addWaypoint}
                  disabled={!waypointName.trim()}
                  className="flex-1 bg-gray-900 dark:bg-white text-white dark:text-gray-900 px-4 py-2.5 rounded-xl hover:bg-gray-800 dark:hover:bg-gray-100 disabled:bg-gray-200 dark:disabled:bg-gray-600 disabled:text-gray-400 disabled:cursor-not-allowed font-medium transition-colors"
                >
                  Add Waypoint
                </button>
                <button
                  onClick={cancelAddWaypoint}
                  className="px-4 py-2.5 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl font-medium transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UnifiedMap;
